import { Component, OnInit, inject, signal, ViewChild, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ApiService, showToast } from './api.service';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { CertificateFormEditorComponent } from './certificate-form-editor.component';
import { CERTIFICATE_PROFILE, defaultCertificateContact } from './certificate-form-defaults';

@Component({
  selector: 'app-admin-project-detail',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, CertificateFormEditorComponent],
  template: `
    <div class="p-5 min-h-full flex flex-col admin-premium-bg">
      <!-- Header -->
      <div class="shrink-0 flex flex-col xl:flex-row xl:items-center justify-between gap-3">
        <div class="min-w-0">
          <a routerLink="/admin/projects" class="inline-flex items-center gap-1.5 text-slate-500 hover:text-[#1A6FA6] font-semibold text-xs mb-1 transition-colors cursor-pointer w-fit"><i class="ti ti-arrow-left"></i> Projects</a>
          <div class="flex flex-wrap items-center gap-2">
            <h1 class="text-xl font-semibold text-slate-950 tracking-tight truncate">{{ project()?.name || 'Loading Project...' }}</h1>
            @if (project()?.status) {
              <span class="px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase border bg-white text-slate-600 border-slate-200 shadow-sm">{{ project()?.status }}</span>
            }
            @if (isArchived()) {
              <span class="px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase border bg-slate-100 text-slate-600 border-slate-200 shadow-sm flex items-center gap-1"><i class="ti ti-archive"></i> Archived</span>
            }
          </div>
          <p class="text-xs text-slate-500 max-w-3xl truncate">{{ project()?.address || 'Project site address will appear here' }}</p>
        </div>
        
        <div class="flex flex-wrap items-center gap-2">
           <button (click)="openEditModal()" [disabled]="isArchived()" class="h-9 px-3 bg-white border border-slate-200 text-slate-700 text-xs font-semibold rounded-lg shadow-sm hover:bg-slate-50 transition-colors cursor-pointer flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"><i class="ti ti-pencil"></i> Edit Project</button>
           <button (click)="completeProject()"
             [disabled]="isArchived() || !canProgressProject() || !isSecondFixDone()"
             class="h-9 px-3 bg-[#1A6FA6] text-white text-xs font-semibold rounded-lg shadow-sm transition-colors cursor-pointer flex items-center gap-2 disabled:opacity-45 disabled:cursor-not-allowed hover:bg-[#0C3A5A]"
            [title]="!canProgressProject() ? 'Customer declaration must be signed or skipped first' : (!isSecondFixDone() ? 'Second Fix must be Completed first' : 'Mark project as Complete')">
             <i class="ti ti-check"></i> Complete
             @if (!canProgressProject() || !isSecondFixDone()) { <i class="ti ti-lock text-xs opacity-70"></i> }
           </button>
           @if (project() && !isArchived()) {
             <button (click)="openArchiveModal()" class="h-9 px-3 bg-white border border-slate-200 text-slate-600 text-xs font-semibold rounded-lg shadow-sm hover:bg-slate-50 transition-colors cursor-pointer flex items-center gap-2"><i class="ti ti-archive"></i> Archive</button>
           }
        </div>
      </div>

      <!-- Context Strip -->
      @if (project()) {
        @if (isArchived()) {
          <div class="mt-4 rounded-xl border border-slate-200 bg-slate-100 px-4 py-3 flex items-start gap-3">
            <span class="w-9 h-9 rounded-lg bg-white text-slate-500 flex items-center justify-center shrink-0"><i class="ti ti-archive text-lg"></i></span>
            <div>
              <p class="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Archived Project</p>
              <p class="text-sm font-semibold text-slate-800 mt-0.5">{{ project()?.archive_remarks || 'No archive remarks recorded.' }}</p>
              @if (project()?.archived_at) {
                <p class="text-xs text-slate-500 mt-1">{{ project()?.archived_at | date:'d MMM y, h:mma' }}</p>
              }
            </div>
          </div>
        }
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mt-4 mb-3 shrink-0">
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-4 py-3 flex items-center gap-3 cursor-pointer group hover:border-[#9ED4EF] transition-colors" (click)="openClientModal()">
            <span class="w-9 h-9 rounded-lg bg-[#EAF5FA] text-[#1A6FA6] flex items-center justify-center"><i class="ti ti-user-circle text-lg"></i></span>
            <div class="min-w-0">
              <p class="text-[10px] font-bold text-[#1A6FA6] uppercase tracking-widest group-hover:underline">Client</p>
              <p class="text-sm font-semibold text-slate-800 truncate">{{ project()?.customer_name || 'N/A' }}</p>
            </div>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-4 py-3 flex items-center gap-3">
            <span class="w-9 h-9 rounded-lg bg-slate-50 text-slate-500 flex items-center justify-center"><i class="ti ti-map-pin text-lg"></i></span>
            <div class="min-w-0">
              <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Site</p>
              <p class="text-sm font-bold text-slate-800 truncate max-w-[200px]">{{ project()?.address || '—' }}</p>
            </div>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-4 py-3 flex items-center gap-3 cursor-pointer group hover:border-[#9ED4EF] transition-colors" (click)="openSafetyModal()">
            <span class="w-9 h-9 rounded-lg flex items-center justify-center"
                  [class.bg-emerald-50]="latestSafetyStatus() === 'Accepted'"
                  [class.text-emerald-600]="latestSafetyStatus() === 'Accepted'"
                  [class.bg-rose-50]="latestSafetyStatus() === 'Declined'"
                  [class.text-rose-600]="latestSafetyStatus() === 'Declined'"
                  [class.bg-amber-50]="!project()?.latest_safety_acknowledgement"
                  [class.text-amber-600]="!project()?.latest_safety_acknowledgement">
              <i [class]="latestSafetyStatus() === 'Declined' ? 'ti ti-shield-x text-lg' : 'ti ti-shield-check text-lg'"></i>
            </span>
            <div class="min-w-0">
              <p class="text-[10px] font-bold text-[#1A6FA6] uppercase tracking-widest group-hover:underline">Safety Ack</p>
              @if (project()?.latest_safety_acknowledgement) {
                <p class="text-sm font-semibold truncate" [class.text-emerald-700]="latestSafetyStatus() === 'Accepted'" [class.text-rose-700]="latestSafetyStatus() === 'Declined'">{{ latestSafetyStatus() === 'Declined' ? 'Declined' : 'Ticked' }}</p>
                <p class="text-[10px] font-semibold text-slate-400">{{ (project().latest_safety_acknowledgement.declined_at || project().latest_safety_acknowledgement.accepted_at) | date:'d MMM y, h:mma' }}</p>
              } @else {
                <p class="text-sm font-semibold text-amber-600">Pending</p>
              }
            </div>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-4 py-3 flex items-center gap-3 cursor-pointer group hover:border-[#9ED4EF]" (click)="openManageWorkers()">
            <span class="w-9 h-9 rounded-lg bg-[#EAF5FA] text-[#1A6FA6] flex items-center justify-center group-hover:bg-[#1A6FA6] group-hover:text-white transition-colors"><i class="ti ti-users text-lg"></i></span>
            <div class="min-w-0">
              <p class="text-[10px] font-bold text-[#1A6FA6] uppercase tracking-widest group-hover:underline">Team</p>
              <p class="text-sm font-semibold text-slate-800">{{ project()?.assigned_workers?.length || 0 }} Assigned</p>
            </div>
          </div>
        </div>
        <div class="grid grid-cols-2 lg:grid-cols-6 gap-2.5 mb-3 shrink-0">
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3 py-2.5">
            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Declaration</p>
            <p class="text-sm font-semibold mt-0.5" [class.text-emerald-700]="canProgressProject()" [class.text-amber-700]="!canProgressProject()">{{ declarationStatus() }}</p>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3 py-2.5">
            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Phase Progress</p>
            <p class="text-sm font-semibold text-slate-900 mt-0.5">{{ approvedPhaseCount() }} / {{ totalPhaseCount() }} approved</p>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3 py-2.5">
            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Review Needed</p>
            <p class="text-sm font-semibold mt-0.5" [class.text-[#1A6FA6]]="submittedPhaseCount() > 0" [class.text-slate-900]="submittedPhaseCount() === 0">{{ submittedPhaseCount() }}</p>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3 py-2.5">
            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Est. Completion</p>
            <p class="text-sm font-semibold text-slate-900 mt-0.5">{{ project()?.estimated_end_date ? (project()?.estimated_end_date | date:'d MMM y') : 'Not set' }}</p>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3 py-2.5">
            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Logged Hours</p>
            <p class="text-sm font-semibold text-slate-900 mt-0.5">{{ loggedHours() }}h</p>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3 py-2.5">
            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Balance Due</p>
            <p class="text-sm font-semibold mt-0.5" [class.text-rose-700]="financialBalanceDue() > 0" [class.text-slate-900]="financialBalanceDue() <= 0">{{ financialBalanceDue() | currency:'NZD':'symbol':'1.2-2' }}</p>
          </div>
        </div>
      }

      <!-- Universal Loader -->
      @if (isLoading()) {
        <div class="flex-1 flex flex-col items-center justify-center py-20 mt-6">
           <i class="ti ti-loader animate-spin text-[40px] text-[#1A6FA6] mb-4"></i>
           <h2 class="text-xl font-bold text-slate-800">Loading project data...</h2>
        </div>
      } @else if (project()) {
        <!-- Nav Tabs & Main Interface (UNLOCKED) -->
        <div class="mt-2 shrink-0">
          <div class="flex gap-1 bg-white border border-[#E7EEF5] rounded-xl p-1 overflow-x-auto w-fit max-w-full shadow-sm">
            <button (click)="switchTab('phases')"
              class="px-3 py-2 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap shrink-0 cursor-pointer"
              [class.bg-[#EAF5FA]]="activeTab() === 'phases'" [class.text-[#1A6FA6]]="activeTab() === 'phases'"
              [class.text-slate-500]="activeTab() !== 'phases'">
              <i class="ti ti-checklist mr-1"></i> Phase Tracking
            </button>
            <button (click)="switchTab('documents')"
              class="px-3 py-2 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap shrink-0 cursor-pointer"
              [class.bg-[#EAF5FA]]="activeTab() === 'documents'" [class.text-[#1A6FA6]]="activeTab() === 'documents'"
              [class.text-slate-500]="activeTab() !== 'documents'">
              <i class="ti ti-file-description mr-1"></i> Documents
            </button>
            <button (click)="switchTab('forms')"
              class="px-3 py-2 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap shrink-0 cursor-pointer"
              [class.bg-[#EAF5FA]]="activeTab() === 'forms'" [class.text-[#1A6FA6]]="activeTab() === 'forms'"
              [class.text-slate-500]="activeTab() !== 'forms'">
              <i class="ti ti-forms mr-1"></i> Forms & Declarations
            </button>
            <button (click)="switchTab('worklog')"
              class="px-3 py-2 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap shrink-0 cursor-pointer"
              [class.bg-[#EAF5FA]]="activeTab() === 'worklog'" [class.text-[#1A6FA6]]="activeTab() === 'worklog'"
              [class.text-slate-500]="activeTab() !== 'worklog'">
              <i class="ti ti-clock-record mr-1"></i> Work Log
            </button>
            <button (click)="switchTab('photos')"
              class="px-3 py-2 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap shrink-0 cursor-pointer"
              [class.bg-[#EAF5FA]]="activeTab() === 'photos'" [class.text-[#1A6FA6]]="activeTab() === 'photos'"
              [class.text-slate-500]="activeTab() !== 'photos'">
              <i class="ti ti-photo mr-1"></i> Photos <span class="ml-1 text-[10px]">{{ projectPhotos().length }}</span>
            </button>
            <button (click)="switchTab('timeline')"
              class="px-3 py-2 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap shrink-0 cursor-pointer"
              [class.bg-[#EAF5FA]]="activeTab() === 'timeline'" [class.text-[#1A6FA6]]="activeTab() === 'timeline'"
              [class.text-slate-500]="activeTab() !== 'timeline'">
              <i class="ti ti-history mr-1"></i> Timeline
            </button>
          </div>
        </div>

        @if (!canProgressProject()) {
          <div class="mt-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex flex-col lg:flex-row lg:items-center justify-between gap-3 shadow-sm">
            <div class="flex items-start gap-3">
              <i class="ti ti-file-alert text-amber-600 text-lg mt-0.5"></i>
              <div>
                <p class="text-xs font-bold text-amber-800 uppercase tracking-widest">Customer declaration pending</p>
                <p class="text-sm text-amber-800/80 mt-0.5">First Fix and Second Fix forms can continue while this is pending. The declaration must be signed or skipped before the overall project can be marked complete.</p>
              </div>
            </div>
            <div class="flex gap-2 shrink-0">
              <button (click)="copyDeclarationLink()" class="h-9 px-3 rounded-lg bg-white border border-amber-200 text-amber-800 text-xs font-bold hover:bg-amber-100 transition-colors cursor-pointer">Copy Link</button>
              <button (click)="skipDeclaration()" class="h-9 px-3 rounded-lg bg-white border border-amber-200 text-amber-800 text-xs font-bold hover:bg-amber-100 transition-colors cursor-pointer">Skip</button>
              <button (click)="sendDeclarationEmail()" [disabled]="isSendingEmail()" class="h-9 px-3 rounded-lg bg-amber-600 text-white text-xs font-bold hover:bg-amber-700 disabled:opacity-60 transition-colors cursor-pointer disabled:cursor-not-allowed">{{ declarationStatus() === 'Sent' ? 'Resend' : 'Send' }}</button>
            </div>
          </div>
        }

        <!-- Main Content Area Scrollable -->
        <div class="flex-1 overflow-auto mt-6 pb-6">
            <!-- PHASES TAB -->
            @if (activeTab() === 'phases') {
              <div class="mb-5 flex justify-between items-center">
                <div>
                  <h2 class="text-xl font-semibold text-slate-950 tracking-tight">Project Execution Phases</h2>
                  <p class="text-sm text-slate-500 mt-1">Track phase status, approvals, and generated forms.</p>
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                 @for (phase of project()?.phases; track phase.id) {
                   <div class="bg-white rounded-xl p-5 shadow-sm flex flex-col transition-all hover:shadow-md border border-[#E7EEF5]"
                        [class.border-amber-200]="phase.phase_name === 'Second-Fix' && !isFirstFixDone()">
                      <div class="flex justify-between items-start mb-4">
                         <div class="flex items-center gap-3">
                           <div class="w-10 h-10 rounded-lg flex items-center justify-center"
                                [class.bg-amber-50]="phase.phase_name === 'Second-Fix' && !isFirstFixDone()"
                                [class.text-amber-400]="phase.phase_name === 'Second-Fix' && !isFirstFixDone()"
                                [class.bg-blue-50]="!(phase.phase_name === 'Second-Fix' && !isFirstFixDone())"
                                [class.text-[#1A6FA6]]="!(phase.phase_name === 'Second-Fix' && !isFirstFixDone())">
                             <i class="ti ti-clipboard-list text-xl"></i>
                           </div>
                           <div>
                              <h3 class="font-semibold text-slate-900 text-base leading-tight">{{ phase.phase_name }}</h3>
                              <p class="text-[10px] font-bold text-slate-400 mt-0.5 uppercase tracking-widest">Phase ID: {{ phase.id }}</p>
                           </div>
                         </div>
                         @if (phase.phase_name === 'Second-Fix' && !isFirstFixDone()) {
                           <span class="flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-50 border border-amber-100 px-2 py-1 rounded-lg">
                             <i class="ti ti-lock text-xs"></i> Locked
                           </span>
                         }
                      </div>

                      <div class="space-y-4 mb-5 flex-1">
                         <div>
                            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Status</p>
                            <div class="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                              <span class="px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border" [ngClass]="phaseStatusClass(phase.status)">
                                {{ phase.status || 'Pending' }}
                              </span>
                              @if (phase.reviewed_by_name && (phase.status === 'Approved' || phase.status === 'Needs Changes')) {
                                <span class="text-[10px] font-semibold text-slate-400 truncate">Reviewed by {{ phase.reviewed_by_name }}</span>
                              }
                            </div>
                            @if (phase.review_notes) {
                              <p class="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                                <span class="font-bold">Office note:</span> {{ phase.review_notes }}
                              </p>
                            }
                         </div>
                      </div>

                      <div class="pt-4 border-t border-slate-100 flex items-center justify-between gap-2">
                          @if (phase.phase_name === 'Second-Fix' && !isFirstFixDone()) {
                            <div class="flex items-center gap-2 text-amber-600 text-xs font-bold">
                              <i class="ti ti-lock"></i> Complete First Fix to unlock
                            </div>
                          } @else if (phase.status === 'Pending') {
                            <button (click)="startPhase(phase)" [disabled]="isArchived()" class="px-3 py-1.5 bg-[#1A6FA6] text-white font-semibold text-xs rounded-lg hover:bg-[#0C3A5A] transition-colors cursor-pointer flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"><i class="ti ti-player-play"></i> Start Phase</button>
                          } @else if (phase.form_data || phase.status !== 'Pending') {
                               <button (click)="openPhaseForm(phase)" [disabled]="isArchived()" class="px-3 py-1.5 bg-white border border-slate-200 text-slate-700 font-semibold text-xs rounded-lg hover:bg-slate-50 transition-colors cursor-pointer flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"><i class="ti ti-file-text"></i> View / Edit Form</button>
                          } @else {
                            <button (click)="openPhaseForm(phase)" [disabled]="isArchived()" class="px-3 py-1.5 bg-white border border-slate-200 text-slate-500 font-semibold text-xs rounded-lg cursor-pointer flex items-center gap-1 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"><i class="ti ti-file-text"></i> Fill Form</button>
                          }
                      </div>
                   </div>
                 } @empty {
                   <div class="col-span-full py-8 text-center text-slate-500 font-medium pb-20">No phases defined for this project.</div>
                 }
              </div>
            }

            <!-- FORMS TAB -->
            @if (activeTab() === 'forms') {
              <div class="mb-5 flex justify-between items-center">
                <div>
                  <h2 class="text-xl font-semibold text-slate-950 tracking-tight">Project Compliance Forms</h2>
                  <p class="text-sm text-slate-500 mt-1">Customer declarations, producer statements, and safety certificates.</p>
                </div>
              </div>
              
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 pb-20">
                 <!-- Customer Declaration Card -->
                 <div class="bg-white rounded-xl p-5 shadow-sm border border-[#E7EEF5] flex flex-col transition-all hover:shadow-md h-full relative overflow-hidden">
                   <div class="flex justify-between items-start mb-5 relative z-10">
                      <div class="flex items-start gap-4">
                        <!-- Status Icon -->
                        @if (declarationStatus() === 'Signed') {
                          <div class="w-11 h-11 bg-emerald-50 rounded-lg border border-emerald-100 flex items-center justify-center text-emerald-500 shrink-0">
                            <i class="ti ti-shield-check-filled text-[24px]"></i>
                          </div>
                        } @else if (declarationStatus() === 'Skipped') {
                          <div class="w-11 h-11 bg-blue-50 rounded-lg border border-blue-100 flex items-center justify-center text-blue-500 shrink-0">
                            <i class="ti ti-player-skip-forward text-[24px]"></i>
                          </div>
                        } @else if (declarationStatus() === 'Sent') {
                          <div class="w-11 h-11 bg-amber-50 rounded-lg border border-amber-100 flex items-center justify-center text-amber-500 shrink-0">
                            <i class="ti ti-mail-forward text-[24px]"></i>
                          </div>
                        } @else {
                          <div class="w-11 h-11 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 shrink-0">
                            <i class="ti ti-file-certificate text-[24px]"></i>
                          </div>
                        }

                        <div>
                           <h3 class="font-semibold text-slate-900 text-base leading-tight mb-1">Customer Declaration</h3>
                           <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider"
                             [class.bg-emerald-100]="declarationStatus() === 'Signed'" [class.text-emerald-700]="declarationStatus() === 'Signed'"
                             [class.bg-blue-100]="declarationStatus() === 'Skipped'" [class.text-blue-700]="declarationStatus() === 'Skipped'"
                             [class.bg-amber-100]="declarationStatus() === 'Sent'" [class.text-amber-700]="declarationStatus() === 'Sent'"
                             [class.bg-slate-100]="declarationStatus() === 'Unsigned'" [class.text-slate-600]="declarationStatus() === 'Unsigned'">
                             {{ declarationStatus() }}
                           </span>
                        </div>
                      </div>
                   </div>
                   
                   <div class="flex-1 space-y-4 relative z-10">
                      <p class="text-sm text-slate-500 font-medium">Customer approval of project terms and scope before commencement.</p>
                       @if (declarationStatus() === 'Signed' && declarationData()) {
                          <div class="bg-slate-50 rounded-xl p-4 border border-slate-100">
                             <div class="grid grid-cols-2 gap-3 mb-2">
                                <div>
                                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Signed By</p>
                                 <p class="text-sm font-bold text-slate-800">{{ declarationData().customer_name }}</p>
                               </div>
                               <div>
                                 <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Date Signed</p>
                                  <p class="text-sm font-bold text-slate-800">{{ declarationData().signed_at | date:'mediumDate' }}</p>
                                </div>
                             </div>
                             <div class="mt-4 pt-4 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Document</p>
                                  <p class="text-xs font-semibold text-slate-700">Contract for Services</p>
                                </div>
                                <div>
                                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Generated</p>
                                  <p class="text-xs font-semibold text-slate-700">{{ generatedPreviewDate | date:'d MMM y, h:mma' }}</p>
                                </div>
                                <div>
                                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Sent</p>
                                  <p class="text-xs font-semibold text-slate-700">{{ declarationData().sent_at ? (declarationData().sent_at | date:'d MMM y, h:mma') : 'Not recorded' }}</p>
                                </div>
                                <div>
                                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Viewed</p>
                                  <p class="text-xs font-semibold text-slate-700">{{ declarationData().viewed_at ? (declarationData().viewed_at | date:'d MMM y, h:mma') : 'Not recorded' }}</p>
                                </div>
                                <div>
                                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">IP</p>
                                  <p class="text-xs font-semibold text-slate-700">{{ declarationData().ip_address || 'Not recorded' }}</p>
                                </div>
                                <div>
                                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Source</p>
                                  <p class="text-xs font-semibold text-slate-700">NZP Admin</p>
                                </div>
                                <div class="sm:col-span-2">
                                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Audit Ref</p>
                                  <p class="text-xs font-semibold text-slate-700">DECL-{{ projectId }}</p>
                                </div>
                             </div>
                          </div>
                       } @else if (declarationStatus() === 'Skipped') {
                          <div class="bg-blue-50 rounded-xl p-4 border border-blue-100">
                             <p class="text-sm font-bold text-blue-800">Declaration skipped by office</p>
                             <p class="text-xs text-blue-700 mt-1">First-Fix can start, and Second-Fix remains locked until First-Fix is approved or completed.</p>
                          </div>
                       }
                   </div>
                   
                   <div class="pt-5 mt-auto flex gap-3 relative z-10">
                     @if (declarationStatus() === 'Signed') {
                         <button (click)="downloadDeclarationPdf()" class="flex-1 h-10 bg-white border border-slate-200 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-center gap-2 cursor-pointer">
                          <i class="ti ti-file-type-pdf text-lg"></i> Open PDF
                        </button>
                     } @else if (declarationStatus() === 'Skipped') {
                         <button disabled class="flex-1 h-10 bg-blue-50 border border-blue-100 text-blue-700 font-semibold rounded-lg flex items-center justify-center gap-2 cursor-not-allowed">
                          <i class="ti ti-player-skip-forward text-lg"></i> Skipped
                        </button>
                     } @else {
                         <button (click)="copyDeclarationLink()" class="flex-1 h-10 bg-white border border-slate-200 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-center gap-2 cursor-pointer">
                          <i class="ti ti-link text-lg"></i> Copy Link
                        </button>
                         <button (click)="skipDeclaration()" class="flex-1 h-10 bg-white border border-slate-200 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-center gap-2 cursor-pointer">
                          <i class="ti ti-player-skip-forward text-lg"></i> Skip
                        </button>
                         <button (click)="sendDeclarationEmail()" [disabled]="isSendingEmail()" class="flex-1 h-10 bg-[#1A6FA6] text-white font-semibold rounded-lg hover:bg-[#0C3A5A] transition-colors disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer disabled:cursor-not-allowed">
                          @if (isSendingEmail()) {
                             <i class="ti ti-loader animate-spin text-lg"></i> {{ declarationStatus() === 'Sent' ? 'Resending...' : 'Sending...' }}
                          } @else {
                             <i class="ti text-lg" [ngClass]="declarationStatus() === 'Sent' ? 'ti-mail-forward' : 'ti-send'"></i> {{ declarationStatus() === 'Sent' ? 'Resend Email' : 'Send Email' }}
                          }
                        </button>
                     }
                   </div>
                 </div>

                 <!-- PS3 Plumbing Card -->
                  <div class="bg-white rounded-xl p-5 shadow-sm border border-[#E7EEF5] flex flex-col transition-all hover:shadow-md h-full relative overflow-hidden">
                    <div class="flex justify-between items-start mb-5 relative z-10">
                       <div class="flex items-start gap-4">
                         @if (getCertStatus('PS3_Plumbing') === 'Completed') {
                            <div class="w-11 h-11 bg-emerald-50 rounded-lg border border-emerald-100 flex items-center justify-center text-emerald-500 shrink-0">
                             <i class="ti ti-checklist text-[24px]"></i>
                           </div>
                         } @else {
                            <div class="w-11 h-11 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 shrink-0">
                             <i class="ti ti-file-description text-[24px]"></i>
                           </div>
                         }
                         <div>
                             <h3 class="font-semibold text-slate-900 text-base leading-tight mb-1">PS3 Plumbing</h3>
                            <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider"
                              [class.bg-emerald-100]="getCertStatus('PS3_Plumbing') === 'Completed'" [class.text-emerald-700]="getCertStatus('PS3_Plumbing') === 'Completed'"
                              [class.bg-slate-100]="getCertStatus('PS3_Plumbing') === 'Pending'" [class.text-slate-600]="getCertStatus('PS3_Plumbing') === 'Pending'">
                              {{ getCertStatus('PS3_Plumbing') }}
                            </span>
                         </div>
                       </div>
                    </div>
                    <div class="flex-1 space-y-4 relative z-10">
                       <p class="text-sm text-slate-500 font-medium pb-4 border-b border-slate-100">Producer statement construction (PS3) - Plumbing testing and compliance verifications.</p>
                       @if (project()?.certificates?.PS3_Plumbing) {
                           <div class="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-widest mt-3"><i class="ti ti-calendar"></i> {{ project().certificates.PS3_Plumbing.updated_at | date }}</div>
                       }
                    </div>
                    <div class="pt-5 mt-auto flex gap-3 relative z-10">
                       <button (click)="openCertForm('PS3_Plumbing')" class="flex-1 h-10 bg-white border border-slate-200 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-center gap-2 cursor-pointer">
                         <i class="ti ti-pencil text-lg"></i> {{ getCertStatus('PS3_Plumbing') === 'Completed' ? 'Edit Form' : 'Fill Form' }}
                       </button>
                       @if (getCertStatus('PS3_Plumbing') === 'Completed') {
                           <button (click)="downloadCertPdf('PS3_Plumbing')" class="w-10 h-10 bg-[#1A6FA6] text-white rounded-lg hover:bg-[#0C3A5A] transition-colors flex items-center justify-center cursor-pointer" title="Open PDF"><i class="ti ti-file-type-pdf text-lg"></i></button>
                       }
                    </div>
                 </div>

                 <!-- PS3 Drainage Card -->
                  <div class="bg-white rounded-xl p-5 shadow-sm border border-[#E7EEF5] flex flex-col transition-all hover:shadow-md h-full relative overflow-hidden">
                    <div class="flex justify-between items-start mb-5 relative z-10">
                       <div class="flex items-start gap-4">
                         @if (getCertStatus('PS3_Drainage') === 'Completed') {
                            <div class="w-11 h-11 bg-emerald-50 rounded-lg border border-emerald-100 flex items-center justify-center text-emerald-500 shrink-0">
                             <i class="ti ti-checklist text-[24px]"></i>
                           </div>
                         } @else {
                            <div class="w-11 h-11 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 shrink-0">
                             <i class="ti ti-file-description text-[24px]"></i>
                           </div>
                         }
                         <div>
                             <h3 class="font-semibold text-slate-900 text-base leading-tight mb-1">PS3 Drainage</h3>
                            <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider"
                              [class.bg-emerald-100]="getCertStatus('PS3_Drainage') === 'Completed'" [class.text-emerald-700]="getCertStatus('PS3_Drainage') === 'Completed'"
                              [class.bg-slate-100]="getCertStatus('PS3_Drainage') === 'Pending'" [class.text-slate-600]="getCertStatus('PS3_Drainage') === 'Pending'">
                              {{ getCertStatus('PS3_Drainage') }}
                            </span>
                         </div>
                       </div>
                    </div>
                    <div class="flex-1 space-y-4 relative z-10">
                       <p class="text-sm text-slate-500 font-medium pb-4 border-b border-slate-100">Producer statement construction (PS3) - Drainage testing and compliance verifications.</p>
                       @if (project()?.certificates?.PS3_Drainage) {
                           <div class="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-widest mt-3"><i class="ti ti-calendar"></i> {{ project().certificates.PS3_Drainage.updated_at | date }}</div>
                       }
                    </div>
                    <div class="pt-5 mt-auto flex gap-3 relative z-10">
                       <button (click)="openCertForm('PS3_Drainage')" class="flex-1 h-10 bg-white border border-slate-200 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-center gap-2 cursor-pointer">
                         <i class="ti ti-pencil text-lg"></i> {{ getCertStatus('PS3_Drainage') === 'Completed' ? 'Edit Form' : 'Fill Form' }}
                       </button>
                       @if (getCertStatus('PS3_Drainage') === 'Completed') {
                           <button (click)="downloadCertPdf('PS3_Drainage')" class="w-10 h-10 bg-[#1A6FA6] text-white rounded-lg hover:bg-[#0C3A5A] transition-colors flex items-center justify-center cursor-pointer" title="Open PDF"><i class="ti ti-file-type-pdf text-lg"></i></button>
                       }
                    </div>
                 </div>

                 <!-- Gas Safety Card -->
                  <div class="bg-white rounded-xl p-5 shadow-sm border border-[#E7EEF5] flex flex-col transition-all hover:shadow-md h-full relative overflow-hidden">
                    <div class="flex justify-between items-start mb-5 relative z-10">
                       <div class="flex items-start gap-4">
                         @if (getCertStatus('Gas_Safety') === 'Completed') {
                            <div class="w-11 h-11 bg-emerald-50 rounded-lg border border-emerald-100 flex items-center justify-center text-emerald-500 shrink-0">
                             <i class="ti ti-flame text-[24px]"></i>
                           </div>
                         } @else {
                            <div class="w-11 h-11 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 shrink-0">
                             <i class="ti ti-file-description text-[24px]"></i>
                           </div>
                         }
                         <div>
                             <h3 class="font-semibold text-slate-900 text-base leading-tight mb-1">Gas Safety</h3>
                            <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider"
                              [class.bg-emerald-100]="getCertStatus('Gas_Safety') === 'Completed'" [class.text-emerald-700]="getCertStatus('Gas_Safety') === 'Completed'"
                              [class.bg-slate-100]="getCertStatus('Gas_Safety') === 'Pending'" [class.text-slate-600]="getCertStatus('Gas_Safety') === 'Pending'">
                              {{ getCertStatus('Gas_Safety') }}
                            </span>
                         </div>
                       </div>
                    </div>
                    <div class="flex-1 space-y-4 relative z-10">
                       <p class="text-sm text-slate-500 font-medium pb-4 border-b border-slate-100">Gasfitting Certificate of Compliance - Gas Safety Certificate requirements.</p>
                       @if (project()?.certificates?.Gas_Safety) {
                           <div class="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-widest mt-3"><i class="ti ti-calendar"></i> {{ project().certificates.Gas_Safety.updated_at | date }}</div>
                       }
                    </div>
                    <div class="pt-5 mt-auto flex gap-3 relative z-10">
                       <button (click)="openCertForm('Gas_Safety')" class="flex-1 h-10 bg-white border border-slate-200 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-center gap-2 cursor-pointer">
                         <i class="ti ti-pencil text-lg"></i> {{ getCertStatus('Gas_Safety') === 'Completed' ? 'Edit Form' : 'Fill Form' }}
                       </button>
                       @if (getCertStatus('Gas_Safety') === 'Completed') {
                           <button (click)="downloadCertPdf('Gas_Safety')" class="w-10 h-10 bg-[#1A6FA6] text-white rounded-lg hover:bg-[#0C3A5A] transition-colors flex items-center justify-center cursor-pointer" title="Open PDF"><i class="ti ti-file-type-pdf text-lg"></i></button>
                       }
                    </div>
                 </div>
              </div>
            }

            <!-- DOCUMENTS TAB -->
            @if (activeTab() === 'documents') {
              <div class="mb-5 flex flex-col xl:flex-row xl:items-end justify-between gap-4">
                <div>
                  <h2 class="text-xl font-semibold text-slate-950 tracking-tight">Project Documents</h2>
                  <p class="text-sm text-slate-500 mt-1">A single place for uploaded files, generated forms, drawings, reports, and quotes.</p>
                </div>
                <div class="flex flex-wrap gap-2">
                  <a routerLink="/admin/ai/as-built" class="px-4 py-2 bg-white border border-slate-200 text-slate-700 font-semibold text-xs rounded-lg hover:bg-slate-50 transition-colors shadow-sm flex items-center gap-2 cursor-pointer">
                    <i class="ti ti-ruler-measure"></i> As-Built
                  </a>
                  <a routerLink="/admin/ai/insurance" class="px-4 py-2 bg-white border border-slate-200 text-slate-700 font-semibold text-xs rounded-lg hover:bg-slate-50 transition-colors shadow-sm flex items-center gap-2 cursor-pointer">
                    <i class="ti ti-shield"></i> Insurance Report
                  </a>
                  <button (click)="hubFileInput.click()" [disabled]="isUploading()" class="px-4 py-2 bg-[#1A6FA6] text-white font-semibold text-xs rounded-lg hover:bg-[#0C3A5A] transition-colors shadow-sm flex items-center gap-2 cursor-pointer disabled:opacity-50">
                    @if (isUploading()) {
                       <i class="ti ti-loader animate-spin"></i> Uploading...
                    } @else {
                       <i class="ti ti-upload"></i> Upload File
                    }
                  </button>
                  <input type="file" #hubFileInput class="hidden" (change)="onFileSelected($event)" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png">
                </div>
              </div>

              <div class="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
                <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm p-5">
                  <div class="flex items-start justify-between gap-3">
                    <div>
                      <p class="text-[10px] uppercase tracking-widest font-semibold text-slate-400">Customer Declaration</p>
                      <h3 class="text-base font-bold text-slate-900 mt-1">{{ declarationStatus() }}</h3>
                    </div>
                    <span class="w-10 h-10 rounded-lg flex items-center justify-center"
                      [class.bg-emerald-50]="declarationStatus() === 'Signed'"
                      [class.text-emerald-600]="declarationStatus() === 'Signed'"
                      [class.bg-blue-50]="declarationStatus() === 'Skipped'"
                      [class.text-blue-600]="declarationStatus() === 'Skipped'"
                      [class.bg-amber-50]="declarationStatus() !== 'Signed' && declarationStatus() !== 'Skipped'"
                      [class.text-amber-600]="declarationStatus() !== 'Signed' && declarationStatus() !== 'Skipped'">
                      <i class="ti" [ngClass]="declarationStatus() === 'Signed' ? 'ti-file-check' : (declarationStatus() === 'Skipped' ? 'ti-player-skip-forward' : 'ti-file-alert')"></i>
                    </span>
                  </div>
                  <div class="mt-4 flex gap-2">
                    @if (declarationStatus() === 'Signed') {
                      <button (click)="downloadDeclarationPdf()" class="flex-1 h-10 rounded-lg bg-[#1A6FA6] text-white text-xs font-bold hover:bg-[#0C3A5A] transition-colors cursor-pointer">Open PDF</button>
                    } @else if (declarationStatus() === 'Skipped') {
                      <button disabled class="flex-1 h-10 rounded-lg bg-blue-50 border border-blue-100 text-blue-700 text-xs font-bold cursor-not-allowed">Skipped</button>
                    } @else {
                      <button (click)="copyDeclarationLink()" class="flex-1 h-10 rounded-lg bg-white border border-slate-200 text-slate-700 text-xs font-bold hover:bg-slate-50 transition-colors cursor-pointer">Copy Link</button>
                      <button (click)="skipDeclaration()" class="flex-1 h-10 rounded-lg bg-white border border-slate-200 text-slate-700 text-xs font-bold hover:bg-slate-50 transition-colors cursor-pointer">Skip</button>
                      <button (click)="sendDeclarationEmail()" class="flex-1 h-10 rounded-lg bg-[#1A6FA6] text-white text-xs font-bold hover:bg-[#0C3A5A] transition-colors cursor-pointer">Send</button>
                    }
                  </div>
                </div>

                <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm p-5">
                  <div class="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <p class="text-[10px] uppercase tracking-widest font-semibold text-slate-400">Phase PDFs</p>
                      <h3 class="text-base font-bold text-slate-900 mt-1">First Fix and Second Fix</h3>
                    </div>
                    <span class="w-10 h-10 rounded-lg bg-sky-50 text-[#1A6FA6] flex items-center justify-center"><i class="ti ti-clipboard-check"></i></span>
                  </div>
                  <div class="space-y-2">
                    @for (phase of project()?.phases || []; track phase.id) {
                      @if (isCorePhase(phase) && hasPhasePdf(phase)) {
                        <button (click)="downloadPhasePdf(phase)" class="w-full h-10 rounded-lg border border-slate-200 bg-slate-50 hover:bg-sky-50 hover:border-sky-200 text-xs font-semibold text-slate-700 hover:text-sky-700 transition-colors flex items-center justify-between px-3 cursor-pointer">
                          <span>{{ phase.phase_name }}</span>
                          <span class="flex items-center gap-1"><i class="ti ti-file-type-pdf"></i> Open</span>
                        </button>
                      }
                    }
                    @if (!hasAnyPhasePdf()) {
                      <p class="text-sm text-slate-500 bg-slate-50 rounded-lg px-3 py-3">No submitted phase forms yet.</p>
                    }
                  </div>
                </div>

                <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm p-5">
                  <div class="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <p class="text-[10px] uppercase tracking-widest font-semibold text-slate-400">Compliance Certificates</p>
                      <h3 class="text-base font-bold text-slate-900 mt-1">PS3 and Gas Safety</h3>
                    </div>
                    <span class="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center"><i class="ti ti-certificate"></i></span>
                  </div>
                  <div class="grid grid-cols-1 gap-2">
                    @for (cert of projectCertTypes; track cert.type) {
                      <div class="h-10 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-between px-3">
                        <span class="text-xs font-bold text-slate-700">{{ cert.label }}</span>
                        @if (getCertStatus(cert.type) === 'Completed') {
                          <button (click)="downloadCertPdf(cert.type)" class="text-xs font-bold text-[#1A6FA6] hover:text-[#0C3A5A] flex items-center gap-1 cursor-pointer"><i class="ti ti-file-type-pdf"></i> Open</button>
                        } @else {
                          <span class="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Pending</span>
                        }
                      </div>
                    }
                  </div>
                </div>
              </div>

              <div class="grid grid-cols-1 2xl:grid-cols-2 gap-6 mb-20">
                <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden">
                  <div class="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                    <div>
                      <h3 class="text-sm font-semibold text-slate-900 uppercase tracking-wider">Uploaded Files</h3>
                      <p class="text-xs text-slate-500 mt-1">Plans, permits, site photos, and supporting files.</p>
                    </div>
                    <span class="px-2.5 py-1 rounded-full bg-slate-100 text-xs font-bold text-slate-500">{{ project()?.documents?.length || 0 }}</span>
                  </div>
                  @if (project()?.documents?.length > 0) {
                    <div class="divide-y divide-slate-100">
                      @for (doc of project()?.documents; track doc.id) {
                        <div class="p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors">
                          <div class="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 border border-slate-200">
                            <i class="ti ti-file-text text-xl"></i>
                          </div>
                          <div class="min-w-0 flex-1">
                            <p class="font-bold text-slate-800 text-sm truncate">{{ doc.name }}</p>
                            <p class="text-xs text-slate-500">{{ doc.document_type || 'General' }} - {{ doc.created_at | date:'mediumDate' }}</p>
                          </div>
                          <a [routerLink]="['/admin/project-doc', doc.id]" [queryParams]="{ projectId: project()?.id }" class="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-sky-50 hover:text-sky-700 hover:border-sky-200 transition-colors shadow-sm cursor-pointer">
                            <i class="ti ti-eye text-lg"></i>
                          </a>
                        </div>
                      }
                    </div>
                  } @else {
                    <div class="p-8 text-center">
                      <div class="w-14 h-14 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3 border border-slate-100">
                        <i class="ti ti-file-upload text-2xl text-slate-400"></i>
                      </div>
                      <p class="text-sm font-bold text-slate-800">No uploaded files yet</p>
                      <p class="text-xs text-slate-500 mt-1">Use Upload File to add project attachments.</p>
                    </div>
                  }
                </div>

                <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden">
                  <div class="px-5 py-4 border-b border-slate-100">
                    <h3 class="text-sm font-semibold text-slate-900 uppercase tracking-wider">Generated Documents</h3>
                    <p class="text-xs text-slate-500 mt-1">As-built drawings and insurance advisory reports attached to this project.</p>
                  </div>
                  <div class="p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <div class="flex items-center justify-between mb-3">
                        <p class="text-xs font-semibold text-slate-400 uppercase tracking-widest">As-Built Drawings</p>
                        <span class="text-xs font-bold text-slate-500">{{ project()?.linked_documents?.as_built?.length || 0 }}</span>
                      </div>
                      <div class="space-y-2">
                        @if (project()?.linked_documents?.as_built?.length > 0) {
                          @for (doc of project()?.linked_documents?.as_built; track doc.id) {
                            <a [routerLink]="['/admin/ai/as-built', doc.id]" class="block rounded-lg border border-slate-200 bg-slate-50 hover:bg-sky-50 hover:border-sky-200 transition-colors p-3">
                              <p class="text-sm font-bold text-slate-800 truncate">{{ doc.title || 'As-built drainage plan' }}</p>
                              <p class="text-xs text-slate-500 mt-1">{{ doc.doc_status || doc.status }} - {{ doc.created_at | date:'mediumDate' }}</p>
                            </a>
                          }
                        } @else {
                           <p class="text-sm text-slate-500 bg-slate-50 rounded-lg px-3 py-4">No as-built drawings attached.</p>
                        }
                      </div>
                    </div>
                    <div>
                      <div class="flex items-center justify-between mb-3">
                        <p class="text-xs font-semibold text-slate-400 uppercase tracking-widest">Insurance Reports</p>
                        <span class="text-xs font-bold text-slate-500">{{ project()?.linked_documents?.insurance_reports?.length || 0 }}</span>
                      </div>
                      <div class="space-y-2">
                        @if (project()?.linked_documents?.insurance_reports?.length > 0) {
                          @for (report of project()?.linked_documents?.insurance_reports; track report.id) {
                            <a [routerLink]="['/admin/ai/insurance', report.id]" class="block rounded-lg border border-slate-200 bg-slate-50 hover:bg-sky-50 hover:border-sky-200 transition-colors p-3">
                              <p class="text-sm font-bold text-slate-800 truncate">{{ report.report_no }}</p>
                              <p class="text-xs text-slate-500 mt-1">{{ report.status }} - {{ report.updated_at | date:'mediumDate' }}</p>
                            </a>
                          }
                        } @else {
                           <p class="text-sm text-slate-500 bg-slate-50 rounded-lg px-3 py-4">No insurance reports attached.</p>
                        }
                      </div>
                    </div>
                  </div>
                </div>

                <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden 2xl:col-span-2">
                  <div class="px-5 py-4 border-b border-slate-100 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                    <div>
                      <h3 class="text-sm font-semibold text-slate-900 uppercase tracking-wider">Commercial Documents</h3>
                      <p class="text-xs text-slate-500 mt-1">Quotes connected with this project.</p>
                    </div>
                    <div class="flex flex-wrap gap-2">
                      <a [routerLink]="['/admin/financials/quotes', 'new']" [queryParams]="{ project_id: project()?.id, model_type: 'Project' }" class="h-9 px-3 rounded-lg bg-[#EAF5FA] border border-[#D5EBF6] text-[#1A6FA6] text-xs font-semibold hover:bg-[#D5EBF6] transition-colors flex items-center gap-2">
                        <i class="ti ti-file-plus"></i> Create Quote
                      </a>
                    </div>
                  </div>
                  <div class="grid grid-cols-2 lg:grid-cols-4 gap-2 p-4 border-b border-slate-100 bg-slate-50/50">
                    <div class="bg-white rounded-lg border border-slate-100 px-3 py-2">
                      <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Quoted</p>
                      <p class="text-sm font-semibold text-slate-900 mt-0.5">{{ (project()?.linked_documents?.financial_summary?.quoted_total || 0) | currency:'NZD':'symbol':'1.2-2' }}</p>
                    </div>
                    <div class="bg-white rounded-lg border border-slate-100 px-3 py-2">
                      <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Accepted</p>
                      <p class="text-sm font-semibold text-slate-900 mt-0.5">{{ project()?.linked_documents?.financial_summary?.accepted_quote_count || 0 }} quote(s)</p>
                    </div>
                  </div>
                  <div class="grid grid-cols-1 xl:grid-cols-2 divide-y xl:divide-y-0 xl:divide-x divide-slate-100">
                    <div class="p-4">
                      <div class="flex items-center justify-between mb-3">
                        <p class="text-xs font-semibold text-slate-400 uppercase tracking-widest">Quotes</p>
                        <span class="text-xs font-bold text-slate-500">{{ project()?.linked_documents?.quotes?.length || 0 }}</span>
                      </div>
                      <div class="space-y-2">
                        @if (project()?.linked_documents?.quotes?.length > 0) {
                          @for (quote of project()?.linked_documents?.quotes; track quote.id) {
                            <a [routerLink]="['/admin/financials/quotes', quote.id]" class="rounded-lg border border-slate-200 bg-slate-50 hover:bg-sky-50 hover:border-sky-200 transition-colors p-3 flex items-center justify-between gap-3">
                              <div class="min-w-0">
                                <p class="text-sm font-bold text-slate-800 truncate">{{ quote.quote_no }}</p>
                                <p class="text-xs text-slate-500">{{ quote.status }} - {{ quote.created_at | date:'mediumDate' }}</p>
                              </div>
                              <span class="text-sm font-semibold text-slate-900">{{ quote.total | currency:'NZD':'symbol':'1.2-2' }}</span>
                            </a>
                          }
                        } @else {
                          <p class="text-sm text-slate-500 bg-slate-50 rounded-lg px-3 py-4">No project quotes yet.</p>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            }

            @if (activeTab() === 'photos') {
              <div class="mb-5 flex items-end justify-between gap-4">
                <div>
                  <h2 class="text-xl font-semibold text-slate-950 tracking-tight">Project Photos</h2>
                  <p class="text-sm text-slate-500 mt-1">Field photos with the worker, Auckland upload time, and their comment.</p>
                </div>
                <span class="px-3 py-1.5 rounded-lg bg-white border border-[#E7EEF5] text-xs font-semibold text-slate-600">{{ projectPhotos().length }} photo(s)</span>
              </div>
              @if (isLoadingProjectPhotos()) {
                <div class="py-20 text-center text-[#1A6FA6]"><i class="ti ti-loader animate-spin text-3xl"></i></div>
              } @else if (projectPhotos().length) {
                <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-20">
                  @for (photo of projectPhotos(); track photo.id; let photoIndex = $index) {
                    <article class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden">
                      <button type="button" (click)="openProjectPhotoViewer(photoIndex)" class="block w-full bg-slate-100 text-left group relative">
                        <img [src]="photo.photo_url || photo.photo_data" [alt]="photo.caption || 'Project photo'" class="w-full h-64 object-cover">
                        <span class="absolute inset-0 bg-slate-950/0 group-hover:bg-slate-950/20 transition-colors flex items-center justify-center"><i class="ti ti-maximize text-2xl text-white opacity-0 group-hover:opacity-100"></i></span>
                      </button>
                      <div class="p-4">
                        <div class="flex items-start gap-3">
                          <span class="w-9 h-9 rounded-full bg-[#EAF5FA] text-[#1A6FA6] flex items-center justify-center font-bold text-xs shrink-0">{{ (photo.uploader_name || '?').charAt(0) }}</span>
                          <div class="min-w-0">
                            <p class="text-sm font-semibold text-slate-900">{{ photo.uploader_name || 'Unknown worker' }}</p>
                            <p class="text-xs text-slate-500 mt-0.5">{{ formatAucklandDateTime(photo.created_at) }}</p>
                          </div>
                        </div>
                        <div class="mt-3 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5">
                          <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Worker comment</p>
                          <p class="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{{ photo.caption || 'No comment added.' }}</p>
                        </div>
                      </div>
                    </article>
                  }
                </div>
              } @else {
                <div class="bg-white rounded-xl border border-dashed border-slate-200 py-20 text-center mb-20">
                  <i class="ti ti-photo-off text-4xl text-slate-300"></i>
                  <p class="text-sm font-semibold text-slate-700 mt-3">No project photos yet</p>
                  <p class="text-xs text-slate-500 mt-1">Photos uploaded by assigned workers will appear here.</p>
                </div>
              }
            }

            @if (activeTab() === 'timeline') {
              <div class="mb-5">
                <h2 class="text-xl font-semibold text-slate-950 tracking-tight">Project Timeline</h2>
                <p class="text-sm text-slate-500 mt-1">A clear audit trail from project setup through field work and commercial activity.</p>
              </div>
              <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden mb-20">
                @if (projectTimeline().length > 0) {
                  <div class="divide-y divide-slate-100">
                    @for (item of projectTimeline(); track item.key) {
                      <div class="p-4 flex items-start gap-3">
                        <span class="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" [ngClass]="item.iconClass">
                          <i class="ti" [ngClass]="item.icon"></i>
                        </span>
                        <div class="min-w-0 flex-1">
                          <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-1">
                            <p class="text-sm font-semibold text-slate-900">{{ item.title }}</p>
                            <p class="text-xs font-semibold text-slate-400">{{ item.date ? (item.date | date:'d MMM y, h:mma') : '-' }}</p>
                          </div>
                          <p class="text-sm text-slate-500 mt-1 leading-relaxed">{{ item.detail }}</p>
                        </div>
                      </div>
                    }
                  </div>
                } @else {
                  <div class="p-10 text-center text-sm text-slate-500">No timeline activity available yet.</div>
                }
              </div>
            }
        </div>
      }

      @if (selectedProjectPhoto()) {
        <div class="fixed inset-0 z-[100] bg-slate-950/90 backdrop-blur-sm flex flex-col" (click)="closeProjectPhotoViewer()">
          <div class="h-16 px-4 md:px-6 flex items-center justify-between gap-3 text-white border-b border-white/10 shrink-0" (click)="$event.stopPropagation()">
            <div class="min-w-0">
              <p class="text-[10px] uppercase tracking-widest text-white/45 font-bold">Project Photo</p>
              <h2 class="text-sm md:text-base font-semibold truncate">{{ selectedProjectPhoto()?.caption || 'Site photo' }}</h2>
            </div>
            <div class="flex items-center gap-2 shrink-0">
              <span class="hidden sm:inline text-xs text-white/45 font-semibold">{{ projectPhotoViewerIndex()! + 1 }} / {{ projectPhotos().length }}</span>
              <button type="button" (click)="downloadSelectedProjectPhoto()" [disabled]="downloadingProjectPhotoId() === selectedProjectPhoto()?.id" class="h-10 px-3 rounded-xl bg-white/10 hover:bg-white/15 text-white flex items-center gap-2 disabled:opacity-50">
                <i [class]="downloadingProjectPhotoId() === selectedProjectPhoto()?.id ? 'ti ti-loader animate-spin' : 'ti ti-download'"></i><span class="hidden sm:inline">Download</span>
              </button>
              <button type="button" (click)="closeProjectPhotoViewer()" class="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/15 text-white flex items-center justify-center"><i class="ti ti-x text-xl"></i></button>
            </div>
          </div>
          <div class="relative flex-1 min-h-0 flex items-center justify-center p-4 md:p-8" (click)="$event.stopPropagation()">
            @if (projectPhotos().length > 1) {
              <button type="button" (click)="showPreviousProjectPhoto()" class="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 border border-white/10 text-white flex items-center justify-center"><i class="ti ti-chevron-left text-2xl"></i></button>
              <button type="button" (click)="showNextProjectPhoto()" class="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 border border-white/10 text-white flex items-center justify-center"><i class="ti ti-chevron-right text-2xl"></i></button>
            }
            <img [src]="selectedProjectPhoto()?.photo_url || selectedProjectPhoto()?.photo_data" [alt]="selectedProjectPhoto()?.caption || 'Project photo'" class="max-w-full max-h-full object-contain rounded-xl shadow-2xl">
          </div>
          <div class="px-4 md:px-6 py-4 border-t border-white/10 text-white bg-slate-950/70" (click)="$event.stopPropagation()">
            <div class="max-w-5xl mx-auto flex items-center justify-between gap-3">
              <div class="min-w-0">
                <p class="text-sm font-semibold">{{ selectedProjectPhoto()?.caption || 'No comment added.' }}</p>
                <p class="text-xs text-white/50 mt-0.5">{{ selectedProjectPhoto()?.uploader_name || 'Unknown uploader' }} · {{ formatAucklandDateTime(selectedProjectPhoto()?.created_at) }}</p>
              </div>
            </div>
          </div>
        </div>
      }

      <!-- Manage Workers/Team Modal -->
      @if (showManageWorkers()) {
        <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div class="bg-white rounded-2xl shadow-xl w-full max-w-4xl border border-slate-100 overflow-hidden flex flex-col max-h-[90vh]">
            <div class="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div>
                <h2 class="text-xl font-bold text-slate-900">Manage Project Team</h2>
                <p class="text-xs text-slate-500 font-medium">Select the technicians globally assigned to this overall project.</p>
              </div>
              <button (click)="closeManageWorkers()" class="w-10 h-10 rounded-full hover:bg-slate-200 flex items-center justify-center text-slate-400 transition-colors cursor-pointer">
                <i class="ti ti-x text-xl"></i>
              </button>
            </div>
            
            <div class="p-6 overflow-y-auto flex-1 space-y-6">
              <section>
                <div class="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <h3 class="text-sm font-bold text-slate-900">Project Access Team</h3>
                    <p class="text-xs text-slate-500">These plumbers can open the project and its files.</p>
                  </div>
                </div>
              <div class="grid grid-cols-2 sm:grid-cols-3 gap-4">
                @for (worker of allWorkers(); track worker.id) {
                  <button (click)="toggleWorkerSelection(worker.id)" 
                    class="p-4 rounded-2xl border-2 transition-all flex flex-col items-center text-center gap-3 relative cursor-pointer"
                    [class]="isWorkerSelected(worker.id) ? 'border-[#1A6FA6] bg-blue-50/50' : 'border-slate-50 hover:border-slate-200 bg-slate-50/30'">
                    
                    @if (isWorkerSelected(worker.id)) {
                      <div class="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#1A6FA6] text-white flex items-center justify-center shadow-sm">
                        <i class="ti ti-check text-[12px]"></i>
                      </div>
                    }

                    <div class="w-12 h-12 rounded-2xl flex items-center justify-center font-bold text-lg shadow-sm"
                      [class]="isWorkerSelected(worker.id) ? 'bg-[#1A6FA6] text-white' : 'bg-white text-slate-300 border border-slate-100'">
                      {{ worker.name.charAt(0).toUpperCase() }}
                    </div>
                    
                    <div>
                      <p class="text-sm font-bold leading-tight" [class.text-[#1A6FA6]]="isWorkerSelected(worker.id)">{{ worker.name }}</p>
                      <p class="text-[10px] font-bold text-slate-400 mt-1 uppercase tracking-widest">{{ worker.role === 'Worker' ? 'Plumber' : worker.role }}</p>
                    </div>
                  </button>
                }
              </div>
              @if (selectedWorkerIds().length) {
                <div class="mt-4 rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                  <h4 class="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Optional Fleet</h4>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    @for (id of selectedWorkerIds(); track id) {
                      <label class="block">
                        <span class="text-[10px] font-bold text-slate-500 block mb-1">{{ workerName(id) }}</span>
                        <select [(ngModel)]="selectedFleetVehicleIds[id]" class="w-full h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#1A6FA6]">
                          <option value="">No vehicle</option>
                          @for (vehicle of fleetVehicles(); track vehicle.id) {
                            <option [value]="vehicle.id">{{ fleetVehicleLabel(vehicle) }}</option>
                          }
                        </select>
                      </label>
                    }
                  </div>
                  @if (fleetVehicles().length === 0) {
                    <p class="text-xs font-semibold text-slate-400 mt-2">No active fleet vehicles available.</p>
                  }
                </div>
              }
              </section>

              <section class="rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
                <div class="flex items-center justify-between gap-3 mb-4">
                  <div>
                    <h3 class="text-sm font-bold text-slate-900">Schedule Project Work</h3>
                    <p class="text-xs text-slate-500">Add a day or date range with clear instructions for the plumber.</p>
                  </div>
                  <span class="text-[10px] font-bold text-[#1A6FA6] bg-white border border-sky-100 rounded-full px-2.5 py-1">{{ projectWorkAssignments().length }} scheduled</span>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label class="block">
                    <span class="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Plumber</span>
                    <select [(ngModel)]="workAssignmentForm.user_id" class="w-full h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#1A6FA6]">
                      <option value="">Select plumber</option>
                      @for (worker of allWorkers(); track worker.id) {
                        <option [value]="worker.id">{{ worker.name }}</option>
                      }
                    </select>
                  </label>
                  <label class="block">
                    <span class="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Status</span>
                    <select [(ngModel)]="workAssignmentForm.status" class="w-full h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#1A6FA6]">
                      <option>Scheduled</option>
                      <option>In Progress</option>
                      <option>Completed</option>
                      <option>Cancelled</option>
                    </select>
                  </label>
                  <label class="block md:col-span-2">
                    <span class="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Fleet optional</span>
                    <select [(ngModel)]="workAssignmentForm.fleet_vehicle_id" class="w-full h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#1A6FA6]">
                      <option value="">No vehicle</option>
                      @for (vehicle of fleetVehicles(); track vehicle.id) {
                        <option [value]="vehicle.id">{{ fleetVehicleLabel(vehicle) }}</option>
                      }
                    </select>
                  </label>
                  <label class="block">
                    <span class="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Start Date</span>
                    <input type="date" [(ngModel)]="workAssignmentForm.start_date" class="w-full h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#1A6FA6]">
                  </label>
                  <label class="block">
                    <span class="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">End Date</span>
                    <input type="date" [(ngModel)]="workAssignmentForm.end_date" class="w-full h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#1A6FA6]">
                  </label>
                  <label class="block md:col-span-2">
                    <span class="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Instructions</span>
                    <textarea [(ngModel)]="workAssignmentForm.instruction" rows="3" placeholder="Example: Complete first-fix pipe setout and upload progress photos."
                              class="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#1A6FA6] resize-none"></textarea>
                  </label>
                </div>

                <div class="flex justify-end gap-2 mt-3">
                  @if (editingWorkAssignmentId()) {
                    <button type="button" (click)="resetWorkAssignmentForm()" class="h-10 px-4 rounded-xl bg-white border border-slate-200 text-slate-600 text-xs font-bold">Cancel Edit</button>
                  }
                  <button type="button" (click)="saveProjectWorkAssignment()" [disabled]="isWorkAssignmentSaving()"
                          class="h-10 px-5 rounded-xl bg-[#1A6FA6] text-white text-xs font-bold disabled:opacity-50 flex items-center gap-2">
                    @if (isWorkAssignmentSaving()) { <i class="ti ti-loader animate-spin"></i> }
                    {{ editingWorkAssignmentId() ? 'Update Schedule' : 'Add Schedule' }}
                  </button>
                </div>

                @if (projectWorkAssignments().length) {
                  <div class="mt-4 space-y-2">
                    @for (item of projectWorkAssignments(); track item.id) {
                      <div class="bg-white border border-slate-100 rounded-xl px-3 py-2 flex items-center justify-between gap-3">
                        <div class="min-w-0">
                          <p class="text-sm font-bold text-slate-800 truncate">{{ item.worker_name || workerName(item.user_id) }} <span class="text-xs font-semibold text-slate-400">{{ formatWorkAssignmentRange(item) }}</span></p>
                          <p class="text-xs text-slate-500 truncate">{{ item.instruction || 'No instruction added.' }}</p>
                        </div>
                        <div class="flex items-center gap-2 shrink-0">
                          <span class="text-[10px] font-bold uppercase rounded-full border px-2 py-1"
                                [class.bg-emerald-50]="item.status === 'Completed'"
                                [class.text-emerald-700]="item.status === 'Completed'"
                                [class.border-emerald-100]="item.status === 'Completed'"
                                [class.bg-rose-50]="item.status === 'Cancelled'"
                                [class.text-rose-700]="item.status === 'Cancelled'"
                                [class.border-rose-100]="item.status === 'Cancelled'"
                                [class.bg-sky-50]="item.status !== 'Completed' && item.status !== 'Cancelled'"
                                [class.text-sky-700]="item.status !== 'Completed' && item.status !== 'Cancelled'"
                                [class.border-sky-100]="item.status !== 'Completed' && item.status !== 'Cancelled'">{{ item.status }}</span>
                          <button type="button" (click)="editProjectWorkAssignment(item)" class="w-8 h-8 rounded-lg bg-slate-50 text-slate-500 hover:text-[#1A6FA6]"><i class="ti ti-pencil"></i></button>
                          <button type="button" (click)="deleteProjectWorkAssignment(item)" class="w-8 h-8 rounded-lg bg-rose-50 text-rose-600"><i class="ti ti-trash"></i></button>
                        </div>
                      </div>
                    }
                  </div>
                }
              </section>
            </div>

            <div class="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
              <button (click)="closeManageWorkers()" class="px-6 py-2.5 rounded-xl font-bold text-slate-600 hover:bg-slate-200 transition-all cursor-pointer">Cancel</button>
              <button (click)="saveAssignments()" [disabled]="isSaving() || selectedWorkerIds().length === 0" class="px-8 py-2.5 rounded-xl font-bold text-white shadow-sm hover:opacity-90 transition-all disabled:opacity-50 flex items-center gap-2 cursor-pointer" style="background: linear-gradient(135deg, #1A6FA6, #0C3A5A);">
                @if (isSaving()) { <i class="ti ti-loader animate-spin text-lg"></i> }
                @else { <i class="ti ti-device-floppy text-lg"></i> Update }
              </button>
            </div>
          </div>
        </div>
      }

    <!-- First/Second Fix Phase Form Workspace -->
      @if (showPhaseFormModal()) {
        <div class="fixed inset-0 bg-[#F7FAFC] z-50 flex flex-col">
          <div class="bg-white w-full h-full flex flex-col overflow-hidden">
            
            <!-- Sticky Header -->
            <div class="p-5 border-b border-slate-100 flex flex-col xl:flex-row xl:items-center justify-between gap-3 shadow-sm bg-white relative z-10 shrink-0">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-[#1A6FA6]">
                  <i class="ti ti-checklist text-xl"></i>
                </div>
                <div>
                  <h2 class="text-xl font-bold text-slate-900">{{ activePhaseName() }} Compliance Report</h2>
                  <p class="text-xs text-slate-500 font-medium">Review, edit, submit, approve, request changes, and generate PDF from this workspace.</p>
                </div>
              </div>
              <div class="flex flex-wrap items-center justify-end gap-2">
                @if (activePhaseData()?.status === 'Submitted') {
                  <button (click)="openPhaseReview(activePhaseData(), 'approve')" class="h-9 px-3 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-100 text-xs font-bold hover:bg-emerald-100 transition-colors cursor-pointer flex items-center gap-2">
                    <i class="ti ti-check"></i> Approve
                  </button>
                  <button (click)="openPhaseReview(activePhaseData(), 'changes')" class="h-9 px-3 rounded-lg bg-amber-50 text-amber-700 border border-amber-100 text-xs font-bold hover:bg-amber-100 transition-colors cursor-pointer flex items-center gap-2">
                    <i class="ti ti-message-report"></i> Needs Changes
                  </button>
                }
                @if (activePhaseData() && hasPhasePdf(activePhaseData())) {
                  <button (click)="downloadPhasePdf(activePhaseData())" class="h-9 px-3 rounded-lg bg-sky-50 text-sky-700 border border-sky-100 text-xs font-bold hover:bg-sky-100 transition-colors cursor-pointer flex items-center gap-2">
                    <i class="ti ti-file-type-pdf"></i> PDF
                  </button>
                }
                <button (click)="showPhaseFormModal.set(false)" class="h-9 px-3 rounded-lg bg-white border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-50 cursor-pointer transition-colors flex items-center gap-2">
                  <i class="ti ti-arrow-left"></i> Back
                </button>
              </div>
            </div>

            <!-- Scrollable Body -->
            <div class="p-6 md:p-8 overflow-y-auto flex-1 bg-slate-50/50 space-y-8">
              <div class="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div>
                      <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Site Name</p>
                      <div class="bg-slate-50 rounded-xl px-4 py-3 text-sm font-bold text-slate-700 border border-slate-100">{{ project()?.address || 'Unknown' }}</div>
                   </div>
                </div>
              </div>

              <!-- TECHNICIAN DETAILS (Global) -->
              <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div class="bg-[#2B9AD2] px-6 py-3">
                  <h3 class="font-bold text-white text-sm tracking-wide">TECHNICIAN DETAILS</h3>
                </div>
                <div class="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                   <div>
                      <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Full Name</p>
                      <input type="text" [(ngModel)]="formTechDetails.fullName" class="w-full text-sm border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#1A6FA6]">
                   </div>
                   <div>
                      <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Phone Number</p>
                      <input type="text" [(ngModel)]="formTechDetails.phone" class="w-full text-sm border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#1A6FA6]">
                   </div>
                   <div>
                      <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Plumbing Licence</p>
                      <input type="text" [(ngModel)]="formTechDetails.plumbingLicence" class="w-full text-sm border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#1A6FA6]">
                   </div>
                   <div>
                      <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Drainage Licence</p>
                      <input type="text" [(ngModel)]="formTechDetails.drainageLicence" class="w-full text-sm border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#1A6FA6]">
                   </div>
                   <div class="sm:col-span-2">
                      <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Gas Fitting Licence</p>
                      <input type="text" [(ngModel)]="formTechDetails.gasLicence" class="w-full text-sm border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#1A6FA6]">
                   </div>
                </div>
              </div>

              <!-- PIPEWORK INSTALLED (Global) -->
              <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div class="p-6">
                   <p class="text-sm font-bold text-slate-800 mb-4 border-b border-slate-100 pb-2">Pipework Installed</p>
                   <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                     @for (opt of pipeworkOptions; track opt) {
                        <label class="flex items-center gap-2 cursor-pointer group" (click)="toggleSelection('pipework', opt)">
                           <div class="w-5 h-5 flex items-center justify-center border rounded transition-colors" [ngClass]="formSelections.pipework[opt] ? 'bg-[#1A6FA6] border-[#1A6FA6]' : 'bg-white border-slate-300 group-hover:border-[#1A6FA6]'">
                              @if (formSelections.pipework[opt]) { <i class="ti ti-check text-white text-[12px] font-bold leading-none"></i> }
                           </div>
                           <span class="text-xs font-bold text-slate-600 select-none">{{ opt }}</span>
                        </label>
                     }
                   </div>
                </div>
              </div>

              @if (activePhaseName() === 'First-Fix') {
                  <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div class="bg-[#2B9AD2] px-6 py-3"><h3 class="font-bold text-white text-sm tracking-wide">HYDRAULIC - PIPEWORK / INSTALLATION</h3></div>
                    <div class="p-6 grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6">
                       @for (opt of hydraulicInstOptions; track opt) {
                          <label class="flex items-start justify-between gap-3 cursor-pointer group border-b border-slate-50 pb-2 hover:bg-slate-50 px-2 rounded" (click)="toggleSelection('hydraulicInst', opt)">
                             <span class="text-xs font-bold text-slate-700 select-none flex-1 mt-0.5">{{ opt }}</span>
                             <div class="shrink-0 w-5 h-5 flex items-center justify-center border rounded transition-colors mt-0.5" [ngClass]="formSelections.hydraulicInst[opt] ? 'bg-[#1A6FA6] border-[#1A6FA6]' : 'bg-white border-slate-300 group-hover:border-[#1A6FA6]'">
                                @if (formSelections.hydraulicInst[opt]) { <i class="ti ti-check text-white text-[12px] font-bold leading-none"></i> }
                             </div>
                          </label>
                       }
                    </div>
                  </div>

                  <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div class="bg-[#2B9AD2] px-6 py-3"><h3 class="font-bold text-white text-sm tracking-wide">HYDRAULIC - PIPEWORK SETOUT</h3></div>
                    <div class="p-6 grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6">
                       @for (opt of hydraulicSetoutOptions; track opt) {
                          <label class="flex items-start justify-between gap-3 cursor-pointer group border-b border-slate-50 pb-2 hover:bg-slate-50 px-2 rounded" (click)="toggleSelection('hydraulicSetout', opt)">
                             <span class="text-xs font-bold text-slate-700 select-none flex-1 mt-0.5">{{ opt }}</span>
                             <div class="shrink-0 w-5 h-5 flex items-center justify-center border rounded transition-colors mt-0.5" [ngClass]="formSelections.hydraulicSetout[opt] ? 'bg-[#1A6FA6] border-[#1A6FA6]' : 'bg-white border-slate-300 group-hover:border-[#1A6FA6]'">
                                @if (formSelections.hydraulicSetout[opt]) { <i class="ti ti-check text-white text-[12px] font-bold leading-none"></i> }
                             </div>
                          </label>
                       }
                    </div>
                  </div>

                  <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div class="bg-[#2B9AD2] px-6 py-3"><h3 class="font-bold text-white text-sm tracking-wide">HYDRAULIC - PIPEWORK TEST & COMPLETION DOCUMENTS</h3></div>
                    <div class="p-6 grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6">
                       @for (opt of hydraulicTestOptions; track opt) {
                          <label class="flex items-start justify-between gap-3 cursor-pointer group border-b border-slate-50 pb-2 hover:bg-slate-50 px-2 rounded" (click)="toggleSelection('hydraulicTest', opt)">
                             <span class="text-xs font-bold text-slate-700 select-none flex-1 mt-0.5">{{ opt }}</span>
                             <div class="shrink-0 w-5 h-5 flex items-center justify-center border rounded transition-colors mt-0.5" [ngClass]="formSelections.hydraulicTest[opt] ? 'bg-[#1A6FA6] border-[#1A6FA6]' : 'bg-white border-slate-300 group-hover:border-[#1A6FA6]'">
                                @if (formSelections.hydraulicTest[opt]) { <i class="ti ti-check text-white text-[12px] font-bold leading-none"></i> }
                             </div>
                          </label>
                       }
                    </div>
                  </div>

                  <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div class="bg-[#2B9AD2] px-6 py-3"><h3 class="font-bold text-white text-sm tracking-wide">DRAINAGE PIPEWORK INSTALLATION</h3></div>
                    <div class="p-6 grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6">
                       @for (opt of drainageInstOptions; track opt) {
                          <label class="flex items-start justify-between gap-3 cursor-pointer group border-b border-slate-50 pb-2 hover:bg-slate-50 px-2 rounded" (click)="toggleSelection('drainageInst', opt)">
                             <span class="text-xs font-bold text-slate-700 select-none flex-1 mt-0.5">{{ opt }}</span>
                             <div class="shrink-0 w-5 h-5 flex items-center justify-center border rounded transition-colors mt-0.5" [ngClass]="formSelections.drainageInst[opt] ? 'bg-[#1A6FA6] border-[#1A6FA6]' : 'bg-white border-slate-300 group-hover:border-[#1A6FA6]'">
                                @if (formSelections.drainageInst[opt]) { <i class="ti ti-check text-white text-[12px] font-bold leading-none"></i> }
                             </div>
                          </label>
                       }
                    </div>
                  </div>

                  <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div class="bg-[#2B9AD2] px-6 py-3"><h3 class="font-bold text-white text-sm tracking-wide">DRAINAGE FINAL FILLOUT</h3></div>
                    <div class="p-6 grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6">
                       @for (opt of drainageFindOptions; track opt) {
                          <label class="flex items-start justify-between gap-3 cursor-pointer group border-b border-slate-50 pb-2 hover:bg-slate-50 px-2 rounded" (click)="toggleSelection('drainageFind', opt)">
                             <span class="text-xs font-bold text-slate-700 select-none flex-1 mt-0.5">{{ opt }}</span>
                             <div class="shrink-0 w-5 h-5 flex items-center justify-center border rounded transition-colors mt-0.5" [ngClass]="formSelections.drainageFind[opt] ? 'bg-[#1A6FA6] border-[#1A6FA6]' : 'bg-white border-slate-300 group-hover:border-[#1A6FA6]'">
                                @if (formSelections.drainageFind[opt]) { <i class="ti ti-check text-white text-[12px] font-bold leading-none"></i> }
                             </div>
                          </label>
                       }
                    </div>
                  </div>
              }
              @else if (activePhaseName() === 'Second-Fix') {
                  <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div class="bg-[#2B9AD2] px-6 py-3"><h3 class="font-bold text-white text-sm tracking-wide">HYDRAULIC - SETOUT</h3></div>
                    <div class="p-6 grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6">
                       @for (opt of hydraulicSetoutSecondFixOptions; track opt) {
                          <label class="flex items-start justify-between gap-3 cursor-pointer group border-b border-slate-50 pb-2 hover:bg-slate-50 px-2 rounded" (click)="toggleSelection('hydraulicSetoutSF', opt)">
                             <span class="text-xs font-bold text-slate-700 select-none flex-1 mt-0.5">{{ opt }}</span>
                             <div class="shrink-0 w-5 h-5 flex items-center justify-center border rounded transition-colors mt-0.5" [ngClass]="formSelections.hydraulicSetoutSF[opt] ? 'bg-[#1A6FA6] border-[#1A6FA6]' : 'bg-white border-slate-300 group-hover:border-[#1A6FA6]'">
                                @if (formSelections.hydraulicSetoutSF[opt]) { <i class="ti ti-check text-white text-[12px] font-bold leading-none"></i> }
                             </div>
                          </label>
                       }
                    </div>
                  </div>

                  <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div class="bg-[#2B9AD2] px-6 py-3"><h3 class="font-bold text-white text-sm tracking-wide">TAPWARE & WHITEWARE - FITOFF</h3></div>
                    <div class="p-6 grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6">
                       @for (opt of tapwareFitoffOptions; track opt) {
                          <label class="flex items-start justify-between gap-3 cursor-pointer group border-b border-slate-50 pb-2 hover:bg-slate-50 px-2 rounded" (click)="toggleSelection('tapwareFitoff', opt)">
                             <span class="text-xs font-bold text-slate-700 select-none flex-1 mt-0.5">{{ opt }}</span>
                             <div class="shrink-0 w-5 h-5 flex items-center justify-center border rounded transition-colors mt-0.5" [ngClass]="formSelections.tapwareFitoff[opt] ? 'bg-[#1A6FA6] border-[#1A6FA6]' : 'bg-white border-slate-300 group-hover:border-[#1A6FA6]'">
                                @if (formSelections.tapwareFitoff[opt]) { <i class="ti ti-check text-white text-[12px] font-bold leading-none"></i> }
                             </div>
                          </label>
                       }
                    </div>
                  </div>

                  <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div class="bg-[#2B9AD2] px-6 py-3"><h3 class="font-bold text-white text-sm tracking-wide">GAS WATER HEATER & REGULATOR INSTALLED - TEST & COMPLETION DOCUMENTS</h3></div>
                    <div class="p-6 grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6">
                       @for (opt of gasWaterHeaterOptions; track opt) {
                          <label class="flex items-start justify-between gap-3 cursor-pointer group border-b border-slate-50 pb-2 hover:bg-slate-50 px-2 rounded" (click)="toggleSelection('gasWaterHeater', opt)">
                             <span class="text-xs font-bold text-slate-700 select-none flex-1 mt-0.5">{{ opt }}</span>
                             <div class="shrink-0 w-5 h-5 flex items-center justify-center border rounded transition-colors mt-0.5" [ngClass]="formSelections.gasWaterHeater[opt] ? 'bg-[#1A6FA6] border-[#1A6FA6]' : 'bg-white border-slate-300 group-hover:border-[#1A6FA6]'">
                                @if (formSelections.gasWaterHeater[opt]) { <i class="ti ti-check text-white text-[12px] font-bold leading-none"></i> }
                             </div>
                          </label>
                       }
                    </div>
                  </div>
              }
              
              <!-- ADDITIONAL NOTES -->
              <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div class="bg-[#2B9AD2] px-6 py-3">
                  <h3 class="font-bold text-white text-sm tracking-wide">ADDITIONAL NOTES</h3>
                </div>
                <div class="p-6">
                  <textarea [(ngModel)]="formNotes" rows="4" class="w-full p-4 rounded-xl border border-slate-200 outline-none focus:border-[#1A6FA6] font-medium text-slate-700 resize-none" placeholder="Provide any additional site notes or observations..."></textarea>
                </div>
              </div>
            </div>

            <!-- Sticky Footer Actions -->
            <div class="p-5 border-t border-slate-100 bg-white flex justify-end gap-3 shrink-0">
               <button (click)="showPhaseFormModal.set(false)" class="px-6 py-3 rounded-xl font-bold text-slate-600 hover:bg-slate-100 transition-all cursor-pointer">Cancel</button>
               <button (click)="savePhaseForm()" [disabled]="isSaving()" class="px-8 py-3 rounded-xl font-bold text-white shadow-sm hover:opacity-90 transition-all disabled:opacity-50 flex items-center gap-2 cursor-pointer" style="background: linear-gradient(135deg, #1A6FA6, #0C3A5A);">
                 @if (isSaving()) { <i class="ti ti-loader animate-spin text-lg"></i> Saving... }
                 @else { <i class="ti ti-device-floppy text-lg"></i> Submit }
               </button>
            </div>
            
          </div>
        </div>
      }
      
      <!-- COMPLIANCE CERTIFICATES MODAL -->
      @if (showCertModal()) {
         <div class="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
           <div class="bg-slate-100 rounded-xl w-full max-w-5xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
             
             <!-- HEADER -->
             <div class="px-6 py-4 bg-white border-b border-slate-200 flex justify-between items-center shrink-0">
               <h2 class="text-xl font-bold text-slate-800">
                  {{ activeCertType() === 'PS3_Plumbing' ? 'Producer statement (PS3) - Plumbing' : activeCertType() === 'PS3_Drainage' ? 'Producer statement (PS3) - Drainage' : 'Gas Safety Certificate' }}
               </h2>
               <button (click)="showCertModal.set(false)" class="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 cursor-pointer">
                  <i class="ti ti-x"></i>
               </button>
             </div>

             <!-- SCROLLABLE FORM PANE -->
             <div class="p-8 overflow-y-auto flex-1 h-[70vh]">
               <app-certificate-form-editor [type]="activeCertType()!" [data]="activeCertificateData()"></app-certificate-form-editor>
               <div class="hidden" aria-hidden="true">
               
               <!-- PS3 PLUMBING / DRAINAGE EXACT LAYOUT -->
               @if (activeCertType() === 'PS3_Plumbing' || activeCertType() === 'PS3_Drainage') {
                  <div class="max-w-[850px] mx-auto bg-white border border-slate-300 shadow-sm p-4 relative font-sans">
                     
                     <div class="flex items-start justify-between mb-1">
                        <h3 class="text-[#24558B] text-[22px] leading-tight font-normal pt-3">
                           Producer statement construction (PS3)<br>
                           {{ activeCertType() === 'PS3_Plumbing' ? 'Plumbing' : 'Drainage' }}
                        </h3>
                        <img [src]="api.assetUrl('/assets/ac_logo.png')" alt="Auckland Council" class="w-40 h-auto object-contain">
                     </div>
                     <p class="text-[10px] italic font-bold text-slate-900 mb-0.5">All sections of this form must be completed.</p>
                     
                     <div class="bg-[#2D5A8C] text-white px-3 py-1 font-bold text-[13px] tracking-wide mb-4">
                        {{ activeCertType() === 'PS3_Plumbing' ? 'TO BE COMPLETED BY THE CERTIFYING PLUMBER WHO COMPLETED THE WORK' : 'TO BE COMPLETED BY THE DRAINLAYER WHO COMPLETED THE WORK' }}
                     </div>

                     <div class="space-y-2 mb-6 text-[13px]">
                        <div class="flex items-center">
                           <div class="w-36 text-slate-800">Author{{ activeCertType() === 'PS3_Plumbing' ? ' name' : "'s name" }}:</div>
                           <input type="text" [ngModel]="activeCertType() === 'PS3_Plumbing' ? certFormPlumbing.authorName : certFormDrainage.authorName" (ngModelChange)="activeCertType() === 'PS3_Plumbing' ? (certFormPlumbing.authorName = $event) : (certFormDrainage.authorName = $event)" class="flex-1 border border-slate-400 px-2 py-1 focus:outline-none focus:border-[#2D5A8C] bg-[#E8F0FE]">
                           <div class="ml-4 mr-2 text-slate-800">Building consent No:</div>
                           <input type="text" [ngModel]="activeCertType() === 'PS3_Plumbing' ? certFormPlumbing.buildingConsentNo : certFormDrainage.buildingConsentNo" (ngModelChange)="activeCertType() === 'PS3_Plumbing' ? (certFormPlumbing.buildingConsentNo = $event) : (certFormDrainage.buildingConsentNo = $event)" class="w-48 border border-slate-400 px-2 py-1 focus:outline-none focus:border-[#2D5A8C] bg-[#E8F0FE]">
                        </div>
                        <div class="flex items-center">
                           <div class="w-36 text-slate-800">Author{{ activeCertType() === 'PS3_Plumbing' ? ' company' : "'s company" }}:</div>
                           <input type="text" [ngModel]="activeCertType() === 'PS3_Plumbing' ? certFormPlumbing.authorCompany : certFormDrainage.authorCompany" (ngModelChange)="activeCertType() === 'PS3_Plumbing' ? (certFormPlumbing.authorCompany = $event) : (certFormDrainage.authorCompany = $event)" class="flex-1 border border-slate-400 px-2 py-1 focus:outline-none focus:border-[#2D5A8C] bg-[#E8F0FE]">
                        </div>
                        <div class="flex items-center">
                           <div class="w-36 text-slate-800">Description of {{ activeCertType() === 'PS3_Plumbing' ? 'plumbing' : 'drainage' }} work:</div>
                           <input type="text" [ngModel]="activeCertType() === 'PS3_Plumbing' ? certFormPlumbing.description : certFormDrainage.description" (ngModelChange)="activeCertType() === 'PS3_Plumbing' ? (certFormPlumbing.description = $event) : (certFormDrainage.description = $event)" class="flex-1 border border-slate-400 px-2 py-1 focus:outline-none focus:border-[#2D5A8C] bg-[#E8F0FE]">
                        </div>
                        <div class="flex items-center">
                           <div class="w-36 text-slate-800">Legal description:</div>
                           <input type="text" [ngModel]="activeCertType() === 'PS3_Plumbing' ? certFormPlumbing.legalDescription : certFormDrainage.legalDescription" (ngModelChange)="activeCertType() === 'PS3_Plumbing' ? (certFormPlumbing.legalDescription = $event) : (certFormDrainage.legalDescription = $event)" class="flex-1 border border-slate-400 px-2 py-1 focus:outline-none focus:border-[#2D5A8C] bg-[#E8F0FE]">
                        </div>
                        <div class="flex items-center">
                           <div class="w-36 text-slate-800">Site address:</div>
                           <input type="text" [ngModel]="activeCertType() === 'PS3_Plumbing' ? certFormPlumbing.siteAddress : certFormDrainage.siteAddress" (ngModelChange)="activeCertType() === 'PS3_Plumbing' ? (certFormPlumbing.siteAddress = $event) : (certFormDrainage.siteAddress = $event)" class="flex-1 border border-slate-400 px-2 py-1 focus:outline-none focus:border-[#2D5A8C] bg-[#E8F0FE]">
                        </div>
                     </div>

                     <p class="text-[12px] text-slate-800 mb-4 leading-relaxed">
                        I have sighted the above building consent and read the attached advice notes. I confirm that the {{ activeCertType() === 'PS3_Plumbing' ? 'plumbing pipe' : 'drainage pipe' }} work and fittings have been selected and constructed to comply with the consented plans to the extent required by the above building consent, and with clause {{ activeCertType() === 'PS3_Plumbing' ? 'G12 of the Building Regulations' : 'E1 and / or G13 of the Building Regulations 1992.' }}
                     </p>
                     
                     <p class="text-[12px] text-slate-800 mb-3">I hereby certify that I have personally tested the {{ activeCertType() === 'PS3_Plumbing' ? 'water supply' : 'drainage' }} system by the method indicated below:</p>

                     <div class="space-y-1 mb-6 text-[12px]">
                        @if (activeCertType() === 'PS3_Plumbing') {
                           <label class="flex items-center gap-3 cursor-pointer"><input type="checkbox" [(ngModel)]="certFormPlumbing.tests.g12" class="w-6 h-6 border bg-[#E8F0FE]"><span class="text-slate-800">NZBC G12 Water Supplies: AS1 clause 7.6, Watertightness</span></label>
                           <label class="flex items-center gap-3 cursor-pointer"><input type="checkbox" [(ngModel)]="certFormPlumbing.tests.as3500_1" class="w-6 h-6 border bg-[#E8F0FE]"><span class="text-slate-800">AS/NZS 3500 1:2021 Water services, clause 17.3.1</span></label>
                           <label class="flex items-center gap-3 cursor-pointer"><input type="checkbox" [(ngModel)]="certFormPlumbing.tests.as3500_4" class="w-6 h-6 border bg-[#E8F0FE]"><span class="text-slate-800">AS/NZS 3500 4:2021 Heated water service, clause 9.3</span></label>
                           <label class="flex items-center gap-3 cursor-pointer"><input type="checkbox" [(ngModel)]="certFormPlumbing.tests.as3500_5" class="w-6 h-6 border bg-[#E8F0FE]"><span class="text-slate-800">AS/NZS 3500 5: 2012 Housing installations, clause 2.24.3.1</span></label>
                           <label class="flex items-center gap-3 cursor-pointer"><input type="checkbox" [(ngModel)]="certFormPlumbing.tests.as2032" class="w-6 h-6 border bg-[#E8F0FE]"><span class="text-slate-800">AS/NZS 2032: 2006 Installation of PVC pipe systems, clause 7.2</span></label>
                           <label class="flex items-center gap-3 cursor-pointer"><input type="checkbox" [(ngModel)]="certFormPlumbing.tests.manufacturers" class="w-6 h-6 border bg-[#E8F0FE]"><span class="text-slate-800">Subject the system to test procedures in accordance with the manufactures requirements and supply a test report</span></label>
                           
                           <div class="flex items-center gap-4 mt-3">
                              <span class="text-slate-800">Test report attached</span>
                              <label class="flex items-center gap-1 cursor-pointer"><input type="radio" [(ngModel)]="certFormPlumbing.testReportAttached" value="Yes" class="w-6 h-6 border bg-[#E8F0FE]"><span class="text-slate-800">Yes</span></label>
                              <label class="flex items-center gap-1 cursor-pointer"><input type="radio" [(ngModel)]="certFormPlumbing.testReportAttached" value="No" class="w-6 h-6 border bg-[#E8F0FE]"><span class="text-slate-800">No</span></label>
                           </div>
                        } @else {
                           <label class="flex items-center gap-3 cursor-pointer"><input type="checkbox" [(ngModel)]="certFormDrainage.tests.vm1_water" class="w-6 h-6 border bg-[#E8F0FE]"><span class="text-slate-800">NZBC E1 Surface Water: VM1, water test in accordance with clause 8.1</span></label>
                           <label class="flex items-center gap-3 cursor-pointer"><input type="checkbox" [(ngModel)]="certFormDrainage.tests.vm1_air" class="w-6 h-6 border bg-[#E8F0FE]"><span class="text-slate-800">NZBC E1 Surface Water: VM1, air test in accordance with clause 8.2 or 8.3;</span></label>
                           <label class="flex items-center gap-3 cursor-pointer"><input type="checkbox" [(ngModel)]="certFormDrainage.tests.as3500_2_water" class="w-6 h-6 border bg-[#E8F0FE]"><span class="text-slate-800">AS/NZS 3500.2: 2021 Sanitary plumbing and drainage, water test in accordance with clause 15.2</span></label>
                           <label class="flex items-center gap-3 cursor-pointer"><input type="checkbox" [(ngModel)]="certFormDrainage.tests.as3500_2_air" class="w-6 h-6 border bg-[#E8F0FE]"><span class="text-slate-800">AS/NZS 3500.2: 2021 Sanitary plumbing and drainage, air pressure test in accordance with clause 15.3;</span></label>
                           <label class="flex items-center gap-3 cursor-pointer"><input type="checkbox" [(ngModel)]="certFormDrainage.tests.as2032" class="w-6 h-6 border bg-[#E8F0FE]"><span class="text-slate-800">AS/NZS 2032: 2006 Installation of PVC pipe systems, clause 7.3</span></label>
                        }
                     </div>

                     <p class="text-[12px] text-slate-800 mb-4 leading-relaxed">
                        {{ activeCertType() === 'PS3_Plumbing' ? 'I understand that this producer statement, if accepted, will be relied on by Council for the purposes of establishing compliance with the above building consent.' : 'I understand that Council will rely upon this producer statement, for the purposes of establishing compliance with the above building consent.' }}
                     </p>

                     <div class="flex items-end gap-4 mb-4 text-[13px]">
                        <span class="w-20 text-slate-800">Signature:</span>
                        <div class="relative flex-1 border border-slate-400 bg-[#E8F0FE] h-[60px] overflow-hidden">
                           @if (!certSignatureEmpty1) {
                              <button (click)="clearCertSignature(1)" class="absolute top-1 right-1 text-xs bg-white text-slate-500 rounded px-2 z-10 hover:bg-slate-200 cursor-pointer">Clear</button>
                           }
                           <canvas #certSignaturePad1
                              (mousedown)="startDrawingCert($event, 1)" (touchstart)="startDrawingCert($event, 1)"
                              (mousemove)="drawCert($event, 1)" (touchmove)="drawCert($event, 1)"
                              (mouseup)="stopDrawingCert(1)" (mouseleave)="stopDrawingCert(1)" (touchend)="stopDrawingCert(1)"
                              class="absolute inset-0 w-full h-full cursor-crosshair touch-none"></canvas>
                        </div>
                        <span class="w-10 text-slate-800 text-right">Date:</span>
                        <input type="date" [ngModel]="activeCertType() === 'PS3_Plumbing' ? certFormPlumbing.date : certFormDrainage.date" (ngModelChange)="activeCertType() === 'PS3_Plumbing' ? (certFormPlumbing.date = $event) : (certFormDrainage.date = $event)" class="w-40 border border-slate-400 px-2 py-1 h-[30px] focus:outline-none focus:border-[#2D5A8C] bg-[#E8F0FE]">
                     </div>

                     <div class="flex items-center mb-6 text-[13px]">
                        <div class="w-64 text-slate-800">Certifying {{ activeCertType() === 'PS3_Plumbing' ? 'plumber' : "drainlayer's" }} registration No:</div>
                        <input type="text" [ngModel]="activeCertType() === 'PS3_Plumbing' ? certFormPlumbing.registrationNo : certFormDrainage.registrationNo" (ngModelChange)="activeCertType() === 'PS3_Plumbing' ? (certFormPlumbing.registrationNo = $event) : (certFormDrainage.registrationNo = $event)" class="flex-1 border border-slate-400 px-2 py-1 focus:outline-none focus:border-[#2D5A8C] bg-[#E8F0FE]">
                     </div>

                     <p class="text-[13px] text-slate-800 mb-2">{{ activeCertType() === 'PS3_Plumbing' ? 'Plumber' : "Drainlayer's" }} contact details:</p>
                     
                     <div class="space-y-2 text-[13px] mb-6">
                        <div class="flex items-center">
                           <div class="w-20 text-slate-800">Address:</div>
                           <input type="text" [ngModel]="activeCertType() === 'PS3_Plumbing' ? certFormPlumbing.contact.address : certFormDrainage.contact.address" (ngModelChange)="activeCertType() === 'PS3_Plumbing' ? (certFormPlumbing.contact.address = $event) : (certFormDrainage.contact.address = $event)" class="flex-1 border border-slate-400 px-2 py-1 focus:outline-none focus:border-[#2D5A8C] bg-[#E8F0FE]">
                           <div class="w-20 text-right pr-2 text-slate-800">Postcode:</div>
                           <input type="text" [ngModel]="activeCertType() === 'PS3_Plumbing' ? certFormPlumbing.contact.postcode : certFormDrainage.contact.postcode" (ngModelChange)="activeCertType() === 'PS3_Plumbing' ? (certFormPlumbing.contact.postcode = $event) : (certFormDrainage.contact.postcode = $event)" class="w-24 border border-slate-400 px-2 py-1 focus:outline-none focus:border-[#2D5A8C] bg-[#E8F0FE]">
                        </div>
                        <div class="flex items-center">
                           <div class="w-20 text-slate-800">Business:</div>
                           <input type="text" [ngModel]="activeCertType() === 'PS3_Plumbing' ? certFormPlumbing.contact.business : certFormDrainage.contact.business" (ngModelChange)="activeCertType() === 'PS3_Plumbing' ? (certFormPlumbing.contact.business = $event) : (certFormDrainage.contact.business = $event)" class="flex-1 border border-slate-400 px-2 py-1 focus:outline-none focus:border-[#2D5A8C] bg-[#E8F0FE]">
                           <div class="w-12 text-right pr-2 text-slate-800">Fax:</div>
                           <input type="text" [ngModel]="activeCertType() === 'PS3_Plumbing' ? certFormPlumbing.contact.fax : certFormDrainage.contact.fax" (ngModelChange)="activeCertType() === 'PS3_Plumbing' ? (certFormPlumbing.contact.fax = $event) : (certFormDrainage.contact.fax = $event)" class="flex-1 border border-slate-400 px-2 py-1 focus:outline-none focus:border-[#2D5A8C] bg-[#E8F0FE]">
                        </div>
                        <div class="flex items-center">
                           <div class="w-20 text-slate-800">Mobile:</div>
                           <input type="text" [ngModel]="activeCertType() === 'PS3_Plumbing' ? certFormPlumbing.contact.mobile : certFormDrainage.contact.mobile" (ngModelChange)="activeCertType() === 'PS3_Plumbing' ? (certFormPlumbing.contact.mobile = $event) : (certFormDrainage.contact.mobile = $event)" class="flex-1 border border-slate-400 px-2 py-1 focus:outline-none focus:border-[#2D5A8C] bg-[#E8F0FE]">
                           <div class="w-12 text-right pr-2 text-slate-800">Email:</div>
                           <input type="email" [ngModel]="activeCertType() === 'PS3_Plumbing' ? certFormPlumbing.contact.email : certFormDrainage.contact.email" (ngModelChange)="activeCertType() === 'PS3_Plumbing' ? (certFormPlumbing.contact.email = $event) : (certFormDrainage.contact.email = $event)" class="flex-1 border border-slate-400 px-2 py-1 focus:outline-none focus:border-[#2D5A8C] bg-[#E8F0FE]">
                        </div>
                     </div>

                     <div class="border border-[#2D5A8C] bg-[#D9E3EF] text-[12px] text-slate-900 mt-2">
                        <div class="bg-[#2D5A8C] text-white px-3 py-1 font-bold tracking-wide">COUNCIL USE ONLY</div>
                        <div class="p-3">
                           <div class="grid grid-cols-[minmax(0,1fr)_230px] gap-6">
                              <div class="space-y-2">
                                 <div class="flex items-center gap-2">
                                    <span class="w-24 font-semibold">Received by:</span>
                                    <span class="flex-1 h-7 border border-[#2D5A8C] bg-[#EEF3FB]"></span>
                                 </div>
                                 <div class="flex items-center gap-2">
                                    <span class="w-24 font-semibold">Signature:</span>
                                    <span class="flex-1 h-7 border border-[#2D5A8C] bg-[#EEF3FB]"></span>
                                 </div>
                              </div>
                              <div class="space-y-2 font-bold justify-self-end w-[230px]">
                                 <div class="grid grid-cols-[88px_34px_24px_30px_24px] items-center gap-1">
                                    <span class="leading-tight text-left">Register<br>checked:</span><span class="text-center">YES</span><span class="h-6 border border-[#2D5A8C] bg-[#EEF3FB]"></span><span class="text-center">NO</span><span class="h-6 border border-[#2D5A8C] bg-[#EEF3FB]"></span>
                                 </div>
                                 <div class="grid grid-cols-[88px_34px_24px_30px_24px] items-center gap-1">
                                    <span class="leading-tight text-left">Registration<br>current:</span><span class="text-center">YES</span><span class="h-6 border border-[#2D5A8C] bg-[#EEF3FB]"></span><span class="text-center">NO</span><span class="h-6 border border-[#2D5A8C] bg-[#EEF3FB]"></span>
                                 </div>
                              </div>
                           </div>
                           <div class="grid grid-cols-[minmax(0,1fr)_154px] items-center gap-6 mt-2 font-bold">
                              <span>Producer statement accepted as establishing compliance with the consented plans:</span>
                              <span class="inline-grid grid-cols-[34px_24px_30px_24px] items-center gap-1 justify-self-end">
                                 <span class="text-center">YES</span><span class="h-6 border border-[#2D5A8C] bg-[#EEF3FB]"></span><span class="text-center">NO</span><span class="h-6 border border-[#2D5A8C] bg-[#EEF3FB]"></span>
                              </span>
                           </div>
                        </div>
                     </div>

                     <div class="mt-5 pt-1 border-t border-slate-900 text-[10px] text-slate-900">
                        <div class="text-center">Auckland Council Building Consents | Private Bag 92300, Auckland 1142 | www.aucklandcouncil.govt.nz | PH 09 301 0101</div>
                        <div class="flex justify-between mt-1">
                           <span>Page 1 of 1</span>
                           <span>February 2024</span>
                           <span>{{ activeCertType() === 'PS3_Plumbing' ? 'AC2311 (v5)' : 'AC2306 (v4)' }}</span>
                        </div>
                     </div>

                  </div>
               }

               <!-- GAS SAFETY CERTIFICATE EXACT LAYOUT -->
               @if (activeCertType() === 'Gas_Safety') {
                  <div class="max-w-[850px] mx-auto bg-white border border-slate-300 shadow-sm p-6 relative font-sans text-[13px] text-slate-800">
                      
                      <div class="bg-[#1a1a1a] text-white p-3 font-bold text-center mb-6 rounded-t tracking-wider">
                          GAS SAFETY CERTIFICATE
                      </div>

                      <div class="grid grid-cols-2 gap-6 mb-4">
                          <div class="flex items-center"><span class="w-32 font-bold">Client Name:</span> <input type="text" [(ngModel)]="certFormGas.clientName" class="flex-1 border-b border-slate-400 px-1 bg-transparent focus:outline-none focus:border-black"></div>
                          <div class="flex items-center"><span class="w-20 font-bold text-right pr-2">Job Ref:</span> <input type="text" [(ngModel)]="certFormGas.jobRef" class="flex-1 border-b border-slate-400 px-1 bg-transparent focus:outline-none focus:border-black"></div>
                      </div>

                      <div class="flex items-center mb-4">
                          <span class="w-48 font-bold">ICP Number: (if applicable)</span>
                          <input type="text" [(ngModel)]="certFormGas.icp" class="flex-1 border-b border-slate-400 px-1 bg-transparent focus:outline-none focus:border-black">
                      </div>

                      <div class="grid grid-cols-12 gap-4 mb-4">
                          <div class="col-span-12 flex items-center">
                              <span class="w-32 font-bold">Address of work:</span>
                              <input type="text" [(ngModel)]="certFormGas.address" class="flex-1 border-b border-slate-400 px-1 bg-transparent focus:outline-none focus:border-black">
                          </div>
                          <div class="col-span-6 flex items-center">
                              <span class="w-32 font-bold">Suburb:</span>
                              <input type="text" [(ngModel)]="certFormGas.suburb" class="flex-1 border-b border-slate-400 px-1 bg-transparent focus:outline-none focus:border-black">
                          </div>
                          <div class="col-span-6 flex items-center">
                              <span class="w-16 font-bold text-right pr-2">City:</span>
                              <input type="text" [(ngModel)]="certFormGas.city" class="flex-1 border-b border-slate-400 px-1 bg-transparent focus:outline-none focus:border-black">
                          </div>
                      </div>

                      <div class="mb-6">
                          <span class="font-bold block mb-1">Description of gasfitting work / appliances:</span>
                          <textarea [(ngModel)]="certFormGas.description" rows="3" class="w-full border border-slate-300 p-2 bg-[#f9fafb] focus:outline-none focus:border-black"></textarea>
                      </div>

                      <div class="grid grid-cols-3 gap-6 mb-8 mt-2 p-4 bg-slate-50 border border-slate-200">
                          <div>
                              <span class="font-bold block mb-1">Gas Supply pressure:</span>
                              <input type="text" [(ngModel)]="certFormGas.gasPressure" class="w-full border-b border-slate-400 px-1 bg-transparent focus:outline-none focus:border-black">
                          </div>
                          <div>
                              <span class="font-bold block mb-1">Risk Class:</span>
                              <div class="flex gap-4 mt-2">
                                  <label class="flex items-center gap-1 cursor-pointer"><input type="radio" name="gasRiskClass" [(ngModel)]="certFormGas.riskClass" value="General" class="w-4 h-4"><span>General</span></label>
                                  <label class="flex items-center gap-1 cursor-pointer"><input type="radio" name="gasRiskClass" [(ngModel)]="certFormGas.riskClass" value="High" class="w-4 h-4"><span>High</span></label>
                              </div>
                          </div>
                          <div>
                              <span class="font-bold block mb-1">Gas Type:</span>
                              <div class="flex flex-col gap-1 mt-1">
                                  <label class="flex items-center gap-1 cursor-pointer"><input type="radio" name="gasType" [(ngModel)]="certFormGas.gasType" value="Natural gas" class="w-4 h-4"><span>Natural gas</span></label>
                                  <label class="flex items-center gap-1 cursor-pointer"><input type="radio" name="gasType" [(ngModel)]="certFormGas.gasType" value="LPG" class="w-4 h-4"><span>LPG</span></label>
                                  <div class="flex items-center gap-2">
                                     <label class="flex items-center gap-1 cursor-pointer"><input type="radio" name="gasType" [(ngModel)]="certFormGas.gasType" value="Other" class="w-4 h-4"><span>Other</span></label>
                                     <input type="text" *ngIf="certFormGas.gasType === 'Other'" [(ngModel)]="certFormGas.gasTypeOther" class="flex-1 border-b border-slate-400 px-1 bg-transparent focus:outline-none focus:border-black" placeholder="Specify">
                                  </div>
                              </div>
                          </div>
                      </div>

                      <div class="space-y-4 mb-8">
                          <!-- Q1 -->
                          <div class="flex justify-between items-start">
                              <span class="flex-1">Was the work carried out in accordance with a certified design?</span>
                              <div class="flex items-center gap-4 ml-4">
                                  <label class="flex items-center gap-1 cursor-pointer"><input type="radio" name="gasCertifiedDesign" [(ngModel)]="certFormGas.certifiedDesign" value="Yes" class="w-5 h-5"><span>Yes</span></label>
                                  <label class="flex items-center gap-1 cursor-pointer"><input type="radio" name="gasCertifiedDesign" [(ngModel)]="certFormGas.certifiedDesign" value="No" class="w-5 h-5"><span>No</span></label>
                              </div>
                          </div>
                          <div class="flex items-center ml-4 text-xs italic" *ngIf="certFormGas.certifiedDesign === 'Yes'">
                              <span class="mr-2">Attach or Provide link to design:</span>
                              <input type="text" [(ngModel)]="certFormGas.certifiedDesignLink" class="flex-1 border-b border-slate-400 px-1 bg-transparent focus:outline-none">
                          </div>

                          <!-- Q2 -->
                          <div class="flex justify-between items-start">
                              <span class="flex-1">Did the work rely on manufacturer's instructions?</span>
                              <div class="flex items-center gap-4 ml-4">
                                  <label class="flex items-center gap-1 cursor-pointer"><input type="radio" name="gasManufacturerInst" [(ngModel)]="certFormGas.manufacturerInst" value="Yes" class="w-5 h-5"><span>Yes</span></label>
                                  <label class="flex items-center gap-1 cursor-pointer"><input type="radio" name="gasManufacturerInst" [(ngModel)]="certFormGas.manufacturerInst" value="No" class="w-5 h-5"><span>No</span></label>
                              </div>
                          </div>
                          <div class="flex items-center ml-4 text-xs italic" *ngIf="certFormGas.manufacturerInst === 'Yes'">
                              <span class="mr-2">Attach or Provide link to instructions:</span>
                              <input type="text" [(ngModel)]="certFormGas.manufacturerInstLink" class="flex-1 border-b border-slate-400 px-1 bg-transparent focus:outline-none">
                          </div>

                          <!-- Q3 -->
                          <div>
                              <span class="block mb-2">Means of compliance:</span>
                              <div class="flex flex-col gap-2 ml-4">
                                  <label class="flex items-center gap-2 cursor-pointer"><input type="radio" name="gasComplianceMeans" [(ngModel)]="certFormGas.complianceMeans" value="AS/NZS 5601.1" class="w-5 h-5"><span>AS/NZS 5601.1</span></label>
                                  <label class="flex items-center gap-2 cursor-pointer"><input type="radio" name="gasComplianceMeans" [(ngModel)]="certFormGas.complianceMeans" value="AS/NZS 5601.2" class="w-5 h-5"><span>AS/NZS 5601.2</span></label>
                                  <div class="flex items-center gap-2">
                                     <label class="flex items-center gap-2 cursor-pointer"><input type="radio" name="gasComplianceMeans" [(ngModel)]="certFormGas.complianceMeans" value="Other" class="w-5 h-5"><span>Other (Specify):</span></label>
                                     <input type="text" *ngIf="certFormGas.complianceMeans === 'Other'" [(ngModel)]="certFormGas.complianceOtherSpec" class="flex-1 border-b border-slate-400 px-1 bg-transparent focus:outline-none">
                                  </div>
                              </div>
                          </div>

                          <!-- Q4 -->
                          <div class="flex justify-between items-start mt-4">
                              <span class="flex-1">Parts of the gas installation that are safe to connect to a gas supply:</span>
                              <div class="flex items-center gap-4 ml-4">
                                  <label class="flex items-center gap-1 cursor-pointer"><input type="radio" name="gasPartsSafe" [(ngModel)]="certFormGas.partsSafe" value="All" class="w-5 h-5"><span>All</span></label>
                                  <div class="flex items-center gap-1">
                                     <label class="flex items-center gap-1 cursor-pointer"><input type="radio" name="gasPartsSafe" [(ngModel)]="certFormGas.partsSafe" value="Parts" class="w-5 h-5"><span>Parts (Specify)</span></label>
                                  </div>
                              </div>
                          </div>
                          <div class="flex items-center ml-4 mt-1" *ngIf="certFormGas.partsSafe === 'Parts'">
                              <input type="text" [(ngModel)]="certFormGas.partsSafeSpec" class="w-full border-b border-slate-400 px-1 bg-transparent focus:outline-none">
                          </div>
                      </div>

                      <div class="grid grid-cols-2 gap-6 mb-8 bg-slate-50 p-4 border border-slate-200">
                          <div>
                             <span class="block font-bold mb-1">Date(s) of gasfitting work:</span>
                             <input type="text" [(ngModel)]="certFormGas.workDates" class="w-full border-b border-slate-400 px-1 bg-transparent focus:outline-none focus:border-black">
                          </div>
                          <div>
                             <span class="block font-bold mb-1 text-xs">Name and Reg Number of anyone who carried out work under supervision:</span>
                             <input type="text" [(ngModel)]="certFormGas.supervisionNameReg" class="w-full border-b border-slate-400 px-1 bg-transparent focus:outline-none focus:border-black">
                          </div>
                      </div>

                      <!-- Certification block 1 -->
                      <div class="border-t-2 border-slate-800 pt-4 mb-6">
                          <p class="font-bold mb-4 uppercase tracking-wide">Certification of gasfitting work</p>
                          <p class="mb-4">I certify that the gasfitting work to which this certificate applies has been done safely and lawfully and in accordance with the Building Act 2004.</p>
                          
                          <div class="grid grid-cols-12 gap-x-6 gap-y-4 items-end">
                              <div class="col-span-5">
                                 <span class="block text-xs font-bold text-slate-500 uppercase">Certifier Name</span>
                                 <input type="text" [(ngModel)]="certFormGas.certifierName" class="w-full border-b border-slate-400 px-1 bg-transparent focus:outline-none text-base">
                              </div>
                              <div class="col-span-3">
                                 <span class="block text-xs font-bold text-slate-500 uppercase">Registration No.</span>
                                 <input type="text" [(ngModel)]="certFormGas.certifierReg" class="w-full border-b border-slate-400 px-1 bg-transparent focus:outline-none text-base">
                              </div>
                              <div class="col-span-4 row-span-2">
                                 <span class="block text-xs font-bold text-slate-500 uppercase">Certifier Signature</span>
                                 <div class="relative w-full border border-slate-400 bg-white h-[60px] overflow-hidden">
                                     @if (!certSignatureEmpty1) {
                                         <button (click)="clearCertSignature(1)" class="absolute top-1 right-1 text-[10px] bg-slate-100 text-slate-500 rounded px-1 z-10 hover:bg-slate-200 cursor-pointer">Clear</button>
                                     }
                                     <canvas #certSignaturePad1
                                        (mousedown)="startDrawingCert($event, 1)" (touchstart)="startDrawingCert($event, 1)"
                                        (mousemove)="drawCert($event, 1)" (touchmove)="drawCert($event, 1)"
                                        (mouseup)="stopDrawingCert(1)" (mouseleave)="stopDrawingCert(1)" (touchend)="stopDrawingCert(1)"
                                        class="absolute inset-0 w-full h-full cursor-crosshair touch-none"></canvas>
                                 </div>
                              </div>
                              <div class="col-span-8">
                                 <span class="block text-xs font-bold text-slate-500 uppercase">Date of form completion</span>
                                 <input type="date" [(ngModel)]="certFormGas.date1" class="w-48 border-b border-slate-400 px-1 bg-transparent focus:outline-none text-base h-[28px]">
                              </div>
                          </div>
                      </div>

                      <!-- Certification block 2 -->
                      <div class="border-t-2 border-slate-800 pt-4 mb-2">
                          <p class="font-bold mb-4 uppercase tracking-wide">Connection of gas installation to a gas supply</p>
                          <p class="mb-4">I confirm I am satisfied that the parts of the gas installation described above are safe to connect to a gas supply.</p>
                          
                          <div class="grid grid-cols-12 gap-x-6 gap-y-4 items-end">
                              <div class="col-span-8">
                                 <span class="block text-xs font-bold text-slate-500 uppercase">Name of person authorising connection</span>
                                 <input type="text" [(ngModel)]="certFormGas.authPersonName" class="w-full border-b border-slate-400 px-1 bg-transparent focus:outline-none text-base">
                              </div>
                              <div class="col-span-4 row-span-2">
                                 <span class="block text-xs font-bold text-slate-500 uppercase">Authorising Signature</span>
                                 <div class="relative w-full border border-slate-400 bg-white h-[60px] overflow-hidden">
                                     @if (!certSignatureEmpty2) {
                                         <button (click)="clearCertSignature(2)" class="absolute top-1 right-1 text-[10px] bg-slate-100 text-slate-500 rounded px-1 z-10 hover:bg-slate-200 cursor-pointer">Clear</button>
                                     }
                                     <canvas #certSignaturePad2
                                        (mousedown)="startDrawingCert($event, 2)" (touchstart)="startDrawingCert($event, 2)"
                                        (mousemove)="drawCert($event, 2)" (touchmove)="drawCert($event, 2)"
                                        (mouseup)="stopDrawingCert(2)" (mouseleave)="stopDrawingCert(2)" (touchend)="stopDrawingCert(2)"
                                        class="absolute inset-0 w-full h-full cursor-crosshair touch-none"></canvas>
                                 </div>
                              </div>
                              <div class="col-span-4">
                                 <span class="block text-xs font-bold text-slate-500 uppercase">Registration No.</span>
                                 <input type="text" [(ngModel)]="certFormGas.authPersonReg" class="w-full border-b border-slate-400 px-1 bg-transparent focus:outline-none text-base">
                              </div>
                              <div class="col-span-4">
                                 <span class="block text-xs font-bold text-slate-500 uppercase">Date of Connection</span>
                                 <input type="date" [(ngModel)]="certFormGas.connectionDate" class="w-48 border-b border-slate-400 px-1 bg-transparent focus:outline-none text-base h-[28px]">
                              </div>
                          </div>
                      </div>

                  </div>
               }

               </div>

             </div>
             
             <!-- ACTIONS -->
             <div class="p-4 bg-white border-t border-slate-200 flex justify-end gap-3 shrink-0">
               <button (click)="showCertModal.set(false)" class="px-6 py-2 rounded-lg font-bold text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer">Close</button>
               <button (click)="saveCertForm()" class="px-8 py-2 rounded-lg font-bold text-white bg-[#2D5A8C] shadow-sm hover:opacity-90 transition-opacity flex items-center gap-2 cursor-pointer">
                  <i class="ti ti-device-floppy"></i> Save Form Data
               </button>
             </div>
           </div>
         </div>
      }

      <!-- ═══════════════════════════════════════ WORK LOG TAB ═══════════════════════════════════════ -->
      @if (project() && activeTab() === 'worklog') {
        <div class="flex-1 overflow-auto mt-6 pb-6">
          <div class="mb-6 flex justify-between items-center">
            <h2 class="text-xl font-bold text-slate-900">Work Log</h2>
          </div>

          @if (!workLogRows().length) {
            <div class="bg-white rounded-2xl p-10 text-center shadow-sm border border-slate-200">
              <i class="ti ti-clock-off text-4xl text-slate-300 mb-3 block"></i>
              <p class="text-slate-500 font-medium">No work sessions recorded yet.</p>
            </div>
          } @else {
            <div class="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <table class="w-full text-sm">
                <thead>
                  <tr class="bg-slate-50 border-b border-slate-100">
                    <th class="px-5 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-widest">Worker</th>
                    <th class="px-5 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-widest">Started</th>
                    <th class="px-5 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-widest">Stopped</th>
                    <th class="px-5 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-widest">Duration</th>
                    <th class="px-5 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-widest">Status</th>
                  </tr>
                </thead>
                <tbody>
                  @for (session of workLogRows(); track session.key) {
                    <tr class="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td class="px-5 py-3 font-bold text-slate-800">
                        <div class="flex items-center gap-2">
                          <div class="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center text-[#1A6FA6] text-xs font-semibold">
                            {{ session.worker_name?.charAt(0) || '?' }}
                          </div>
                          {{ session.worker_name }}
                        </div>
                        @if (session.notes) {
                          <p class="text-[11px] font-normal text-slate-400 mt-1 ml-9">{{ session.notes }}</p>
                        }
                      </td>
                      <td class="px-5 py-3 text-slate-600">{{ formatWorkLogStarted(session) }}</td>
                      <td class="px-5 py-3 text-slate-600">{{ session.stopped_at ? formatAucklandDateTime(session.stopped_at) : '—' }}</td>
                      <td class="px-5 py-3 font-bold text-[#1A6FA6]">
                        @if (session.duration_hours > 0) {
                          {{ session.duration_hours | number:'1.2-2' }}h
                        } @else {
                          {{ session.status === 'active' ? 'Active now' : '—' }}
                        }
                      </td>
                      <td class="px-5 py-3">
                        <span class="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase"
                              [class.bg-emerald-100]="session.status === 'stopped'"
                              [class.text-emerald-700]="session.status === 'stopped'"
                              [class.bg-amber-100]="session.status === 'paused'"
                              [class.text-amber-700]="session.status === 'paused'"
                              [class.bg-blue-100]="session.status === 'active'"
                              [class.text-blue-700]="session.status === 'active'"
                              [class.bg-slate-100]="session.status === 'logged'"
                              [class.text-slate-700]="session.status === 'logged'">
                          {{ session.status }}
                        </span>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </div>
      }

      <!-- ═══════════════════════════════════════ EDIT PROJECT MODAL ═══════════════════════════════════════ -->
      @if (showPhaseReviewModal()) {
        <div class="fixed inset-0 z-[110] bg-slate-900/55 backdrop-blur-sm flex items-center justify-center p-4">
          <div class="bg-white rounded-2xl shadow-2xl w-full max-w-xl border border-slate-100 overflow-hidden">
            <div class="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Office Review</p>
                <h2 class="text-lg font-semibold text-slate-950 mt-0.5">{{ reviewPhase()?.phase_name || 'Project Phase' }}</h2>
              </div>
              <button (click)="closePhaseReview()" class="w-9 h-9 rounded-lg bg-slate-50 text-slate-500 hover:bg-slate-100 flex items-center justify-center cursor-pointer">
                <i class="ti ti-x"></i>
              </button>
            </div>
            <div class="p-5 space-y-4">
              <div class="grid grid-cols-2 gap-3">
                <div class="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Status</p>
                  <p class="text-sm font-semibold text-slate-900 mt-0.5">{{ reviewPhase()?.status || '-' }}</p>
                </div>
                <div class="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Signature</p>
                  <p class="text-sm font-semibold mt-0.5" [class.text-emerald-700]="reviewPhaseHasSignature()" [class.text-amber-700]="!reviewPhaseHasSignature()">{{ reviewPhaseHasSignature() ? 'Captured' : 'Missing' }}</p>
                </div>
              </div>
              @if (reviewPhase()?.form_data?.notes) {
                <div class="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3">
                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Field Notes</p>
                  <p class="text-sm text-slate-700 leading-relaxed">{{ reviewPhase()?.form_data?.notes }}</p>
                </div>
              }
              <div>
                <label class="block text-xs font-semibold text-slate-600 mb-1.5">Review note</label>
                <textarea [(ngModel)]="phaseReviewNotes" rows="4"
                  [placeholder]="phaseReviewMode() === 'changes' ? 'Explain what needs to be corrected before approval...' : 'Optional approval note for audit history...'"
                  class="w-full p-3 rounded-xl border border-slate-200 bg-slate-50 outline-none focus:bg-white focus:border-[#1A6FA6] transition-colors text-sm resize-none"></textarea>
              </div>
            </div>
            <div class="px-5 py-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
              <button (click)="closePhaseReview()" class="h-10 px-4 rounded-lg bg-white border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-100 cursor-pointer">Cancel</button>
              @if (phaseReviewMode() === 'changes') {
                <button (click)="submitPhaseReview('Needs Changes')" [disabled]="isReviewSaving()" class="h-10 px-5 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-60 cursor-pointer flex items-center gap-2">
                  @if (isReviewSaving()) { <i class="ti ti-loader animate-spin"></i> }
                  Request Changes
                </button>
              } @else {
                <button (click)="submitPhaseReview('Approved')" [disabled]="isReviewSaving()" class="h-10 px-5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60 cursor-pointer flex items-center gap-2">
                  @if (isReviewSaving()) { <i class="ti ti-loader animate-spin"></i> }
                  Approve Phase
                </button>
              }
            </div>
          </div>
        </div>
      }

      @if (showSafetyModal()) {
        <div class="fixed inset-0 z-[105] bg-slate-900/55 backdrop-blur-sm flex items-center justify-center p-4">
          <div class="bg-white rounded-2xl shadow-2xl w-full max-w-2xl border border-slate-100 overflow-hidden">
            <div class="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Pre-start Safety Acknowledgement</p>
                <h2 class="text-lg font-semibold text-slate-950 mt-0.5">
                  {{ latestSafetyStatus() === 'Declined' ? 'Safety declined' : project()?.latest_safety_acknowledgement ? 'Safety accepted' : 'Safety pending' }}
                </h2>
              </div>
              <button (click)="closeSafetyModal()" class="w-9 h-9 rounded-lg bg-slate-50 text-slate-500 hover:bg-slate-100 flex items-center justify-center cursor-pointer">
                <i class="ti ti-x"></i>
              </button>
            </div>
            <div class="p-5 space-y-4">
              @if (project()?.latest_safety_acknowledgement) {
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div class="rounded-xl border px-4 py-3"
                       [class.bg-emerald-50]="latestSafetyStatus() === 'Accepted'"
                       [class.border-emerald-100]="latestSafetyStatus() === 'Accepted'"
                       [class.bg-rose-50]="latestSafetyStatus() === 'Declined'"
                       [class.border-rose-100]="latestSafetyStatus() === 'Declined'">
                    <p class="text-[10px] font-bold uppercase tracking-widest" [class.text-emerald-700]="latestSafetyStatus() === 'Accepted'" [class.text-rose-700]="latestSafetyStatus() === 'Declined'">{{ latestSafetyStatus() === 'Declined' ? 'Declined By' : 'Accepted By' }}</p>
                    <p class="text-sm font-semibold mt-1" [class.text-emerald-950]="latestSafetyStatus() === 'Accepted'" [class.text-rose-950]="latestSafetyStatus() === 'Declined'">{{ project().latest_safety_acknowledgement.worker_name || 'Worker' }}</p>
                  </div>
                  <div class="rounded-xl border px-4 py-3"
                       [class.bg-emerald-50]="latestSafetyStatus() === 'Accepted'"
                       [class.border-emerald-100]="latestSafetyStatus() === 'Accepted'"
                       [class.bg-rose-50]="latestSafetyStatus() === 'Declined'"
                       [class.border-rose-100]="latestSafetyStatus() === 'Declined'">
                    <p class="text-[10px] font-bold uppercase tracking-widest" [class.text-emerald-700]="latestSafetyStatus() === 'Accepted'" [class.text-rose-700]="latestSafetyStatus() === 'Declined'">{{ latestSafetyStatus() === 'Declined' ? 'Declined At' : 'Accepted At' }}</p>
                    <p class="text-sm font-semibold mt-1" [class.text-emerald-950]="latestSafetyStatus() === 'Accepted'" [class.text-rose-950]="latestSafetyStatus() === 'Declined'">{{ (project().latest_safety_acknowledgement.declined_at || project().latest_safety_acknowledgement.accepted_at) | date:'d MMM y, h:mma' }}</p>
                  </div>
                </div>
                <div class="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3">
                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Acknowledgement Text</p>
                  <p class="text-sm text-slate-700 leading-relaxed">{{ project().latest_safety_acknowledgement.statement }}</p>
                </div>
                @if (latestSafetyStatus() === 'Declined') {
                  <div class="rounded-xl bg-rose-50 border border-rose-100 px-4 py-3">
                    <div class="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p class="text-[10px] font-bold text-rose-700 uppercase tracking-widest mb-2">Decline Reason</p>
                        <p class="text-sm text-rose-900 leading-relaxed">{{ project().latest_safety_acknowledgement.decline_reason || '-' }}</p>
                      </div>
                      @if (!project().latest_safety_acknowledgement.resolved_at) {
                        <div class="flex flex-wrap gap-2">
                          <button (click)="sendSafetyDeclineEmail()" [disabled]="isSendingSafetyEmail() || isResolvingSafety()" class="h-9 px-3 rounded-lg bg-rose-600 text-white text-xs font-bold disabled:opacity-60 flex items-center gap-2">
                            @if (isSendingSafetyEmail()) { <i class="ti ti-loader animate-spin"></i> } @else { <i class="ti ti-mail"></i> }
                            Email Customer
                          </button>
                          <button (click)="resolveSafetyConcern()" [disabled]="isSendingSafetyEmail() || isResolvingSafety()" class="h-9 px-3 rounded-lg bg-emerald-600 text-white text-xs font-bold disabled:opacity-60 flex items-center gap-2">
                            @if (isResolvingSafety()) { <i class="ti ti-loader animate-spin"></i> } @else { <i class="ti ti-check"></i> }
                            Mark Resolved
                          </button>
                        </div>
                      }
                    </div>
                    @if (project().latest_safety_acknowledgement.decline_photo_data) {
                      <img [src]="project().latest_safety_acknowledgement.decline_photo_data" alt="Safety concern photo" class="mt-3 max-h-72 rounded-xl border border-rose-100 object-contain bg-white">
                    }
                    @if (project().latest_safety_acknowledgement.customer_email_sent_at) {
                      <p class="text-xs text-rose-700 font-bold mt-3">Customer emailed {{ project().latest_safety_acknowledgement.customer_email_sent_at | date:'d MMM y, h:mma' }}</p>
                    }
                    @if (project().latest_safety_acknowledgement.resolved_at) {
                      <div class="mt-3 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-emerald-800">
                        <p class="text-xs font-bold">Resolved {{ project().latest_safety_acknowledgement.resolved_at | date:'d MMM y, h:mma' }} by {{ project().latest_safety_acknowledgement.resolved_by_name || 'Office' }}</p>
                        @if (project().latest_safety_acknowledgement.resolution_notes) {
                          <p class="text-xs mt-1">{{ project().latest_safety_acknowledgement.resolution_notes }}</p>
                        }
                      </div>
                    }
                  </div>
                }
              } @else {
                <div class="rounded-xl bg-amber-50 border border-amber-100 px-4 py-5 text-amber-800">
                  <p class="text-sm font-semibold">No safety acknowledgement has been recorded for this project yet.</p>
                  <p class="text-sm mt-1">The worker will be asked to accept the safety acknowledgement before starting project work from the mobile app.</p>
                </div>
              }
            </div>
            <div class="px-5 py-4 border-t border-slate-100 bg-slate-50 flex justify-end">
              <button (click)="closeSafetyModal()" class="h-10 px-5 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-100 cursor-pointer">Close</button>
            </div>
          </div>
        </div>
      }

      @if (showClientModal()) {
        <div class="fixed inset-0 z-[105] bg-slate-900/55 backdrop-blur-sm flex items-center justify-center p-4">
          <div class="bg-white rounded-2xl shadow-2xl w-full max-w-2xl border border-slate-100 overflow-hidden">
            <div class="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Client Details</p>
                <h2 class="text-lg font-semibold text-slate-950 mt-0.5">{{ project()?.customer_name || 'Client' }}</h2>
              </div>
              <button (click)="closeClientModal()" class="w-9 h-9 rounded-lg bg-slate-50 text-slate-500 hover:bg-slate-100 flex items-center justify-center cursor-pointer">
                <i class="ti ti-x"></i>
              </button>
            </div>
            <div class="p-5 space-y-4">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div class="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3">
                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Primary Name</p>
                  <p class="text-sm font-semibold text-slate-900 mt-1">{{ project()?.customer_name || '-' }}</p>
                </div>
                <div class="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3">
                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Customer ID</p>
                  <p class="text-sm font-semibold text-slate-900 mt-1">{{ project()?.customer_id || '-' }}</p>
                </div>
                <div class="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3 sm:col-span-2">
                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Site Address</p>
                  <p class="text-sm font-semibold text-slate-900 mt-1">{{ project()?.address || '-' }}</p>
                </div>
              </div>

              <div>
                <div class="flex items-center justify-between mb-2">
                  <p class="text-xs font-bold text-slate-500 uppercase tracking-widest">Project Contacts</p>
                  <span class="text-xs font-bold text-slate-400">{{ projectContacts().length }}</span>
                </div>
                @if (projectContacts().length > 0) {
                  <div class="divide-y divide-slate-100 rounded-xl border border-slate-100 overflow-hidden">
                    @for (contact of projectContacts(); track contact.name + contact.email + contact.phone) {
                      <div class="p-4 bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div class="min-w-0">
                          <div class="flex items-center gap-2">
                            <p class="text-sm font-semibold text-slate-900 truncate">{{ contact.name || 'Unnamed contact' }}</p>
                            <span class="px-2 py-0.5 rounded-md bg-[#EAF5FA] text-[#1A6FA6] text-[10px] font-bold uppercase">{{ contact.contact_type || 'Contact' }}</span>
                          </div>
                          <p class="text-xs text-slate-500 mt-1 truncate">{{ contact.email || 'No email' }}</p>
                          <p class="text-xs text-slate-500 mt-0.5">{{ contact.phone || 'No phone' }}</p>
                        </div>
                        <div class="flex gap-2 shrink-0">
                          @if (contact.phone) {
                            <a [href]="'tel:' + contact.phone" class="h-9 px-3 rounded-lg bg-white border border-slate-200 text-slate-700 text-xs font-bold hover:bg-slate-50 cursor-pointer flex items-center gap-1"><i class="ti ti-phone"></i> Call</a>
                          }
                          @if (contact.email) {
                            <a [href]="'mailto:' + contact.email" class="h-9 px-3 rounded-lg bg-white border border-slate-200 text-slate-700 text-xs font-bold hover:bg-slate-50 cursor-pointer flex items-center gap-1"><i class="ti ti-mail"></i> Email</a>
                          }
                        </div>
                      </div>
                    }
                  </div>
                } @else {
                  <div class="rounded-xl bg-slate-50 border border-slate-100 px-4 py-5 text-sm text-slate-500">No project contacts have been added yet.</div>
                }
              </div>
            </div>
            <div class="px-5 py-4 border-t border-slate-100 bg-slate-50 flex justify-end">
              <button (click)="closeClientModal()" class="h-10 px-5 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-100 cursor-pointer">Close</button>
            </div>
          </div>
        </div>
      }

      @if (showCompleteModal()) {
        <div class="fixed inset-0 z-[105] bg-slate-900/55 backdrop-blur-sm flex items-center justify-center p-4">
          <div class="bg-white rounded-2xl shadow-2xl w-full max-w-xl border border-slate-100 overflow-hidden">
            <div class="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Complete Project</p>
                <h2 class="text-lg font-semibold text-slate-950 mt-0.5">Final readiness check</h2>
              </div>
              <button (click)="showCompleteModal.set(false)" class="w-9 h-9 rounded-lg bg-slate-50 text-slate-500 hover:bg-slate-100 flex items-center justify-center cursor-pointer">
                <i class="ti ti-x"></i>
              </button>
            </div>
            <div class="p-5 space-y-2">
              @for (item of completionChecklist(); track item.label) {
                <div class="rounded-xl border px-4 py-3 flex items-start gap-3" [class.bg-emerald-50]="item.done" [class.border-emerald-100]="item.done" [class.bg-amber-50]="!item.done" [class.border-amber-100]="!item.done">
                  <i class="ti mt-0.5" [ngClass]="item.done ? 'ti-circle-check text-emerald-600' : 'ti-alert-circle text-amber-600'"></i>
                  <div>
                    <p class="text-sm font-semibold" [class.text-emerald-900]="item.done" [class.text-amber-900]="!item.done">{{ item.label }}</p>
                    <p class="text-xs mt-0.5" [class.text-emerald-700]="item.done" [class.text-amber-700]="!item.done">{{ item.detail }}</p>
                  </div>
                </div>
              }
            </div>
            <div class="px-5 py-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
              <button (click)="showCompleteModal.set(false)" class="h-10 px-4 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-100 cursor-pointer">Cancel</button>
              <button (click)="confirmCompleteProject()" class="h-10 px-5 rounded-lg bg-[#1A6FA6] text-white text-sm font-semibold hover:bg-[#0C3A5A] cursor-pointer">Complete Project</button>
            </div>
          </div>
        </div>
      }

      @if (showEditModal()) {
        <div class="fixed inset-0 z-50 flex items-center justify-center p-4" style="background:rgba(15,23,42,0.65);">
          <div class="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden">
            <div class="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
              <h2 class="text-xl font-semibold text-slate-900">Edit Project</h2>
              <button (click)="showEditModal.set(false)" class="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors cursor-pointer">
                <i class="ti ti-x"></i>
              </button>
            </div>
            <div class="p-6 space-y-4">
              <div>
                <label class="block text-xs font-bold text-slate-500 mb-1.5">Project Name</label>
                <input type="text" [(ngModel)]="editForm.name" class="w-full h-11 px-4 rounded-xl border border-slate-200 focus:border-[#1A6FA6] outline-none text-sm font-medium">
              </div>
              <div>
                <label class="block text-xs font-bold text-slate-500 mb-1.5">Site Address</label>
                <input type="text" [(ngModel)]="editForm.address" class="w-full h-11 px-4 rounded-xl border border-slate-200 focus:border-[#1A6FA6] outline-none text-sm font-medium">
              </div>
              <div>
                <label class="block text-xs font-bold text-slate-500 mb-1.5">Status</label>
                <select [(ngModel)]="editForm.status" class="w-full h-11 px-4 rounded-xl border border-slate-200 focus:border-[#1A6FA6] outline-none text-sm font-bold">
                  <option value="Planning">Planning</option>
                  <option value="In Progress">In Progress</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>
              <div>
                <label class="block text-xs font-bold text-slate-500 mb-1.5">Estimated Completion Date</label>
                <input type="date" [(ngModel)]="editForm.estimated_end_date" class="w-full h-11 px-4 rounded-xl border border-slate-200 focus:border-[#1A6FA6] outline-none text-sm font-medium">
              </div>
              <div>
                <label class="block text-xs font-bold text-slate-500 mb-1.5">Description</label>
                <textarea [(ngModel)]="editForm.description" rows="3" class="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1A6FA6] outline-none text-sm resize-none"></textarea>
              </div>
            </div>
            <div class="px-6 py-4 border-t border-slate-100 flex gap-3 justify-end">
              <button (click)="showEditModal.set(false)" class="px-5 py-2 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 transition-colors cursor-pointer">Cancel</button>
              <button (click)="saveEditForm()" [disabled]="isSavingEdit()"
                      class="px-6 py-2 text-white font-bold rounded-xl shadow-sm hover:opacity-90 transition-opacity flex items-center gap-2 disabled:opacity-50"
                      style="background:linear-gradient(135deg,#1A6FA6,#0C3A5A);">
                @if (isSavingEdit()) {
                  <i class="ti ti-loader animate-spin"></i> Saving...
                } @else {
                  <i class="ti ti-device-floppy"></i> Save Changes
                }
              </button>
            </div>
          </div>
        </div>
      }

      @if (showArchiveModal()) {
        <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div class="bg-white rounded-3xl shadow-xl w-full max-w-lg border border-slate-100 overflow-hidden">
            <div class="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div>
                <h2 class="text-lg font-bold text-slate-900">Archive Project</h2>
                <p class="text-xs text-slate-500 font-medium">This removes the project from active operational lists.</p>
              </div>
              <button (click)="closeArchiveModal()" class="w-9 h-9 rounded-full hover:bg-slate-200 flex items-center justify-center text-slate-400">
                <i class="ti ti-x"></i>
              </button>
            </div>
            <div class="p-5">
              <label class="block">
                <span class="text-sm font-semibold text-slate-700 mb-1 block">Archive remarks *</span>
                <textarea [(ngModel)]="archiveRemarks" rows="4" class="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:bg-white focus:border-[#1A6FA6] resize-none" placeholder="Example: duplicate project, cancelled by customer, superseded by another record..."></textarea>
              </label>
            </div>
            <div class="p-5 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3">
              <button (click)="closeArchiveModal()" class="h-10 px-4 rounded-xl text-slate-600 text-sm font-bold">Cancel</button>
              <button (click)="archiveProject()" [disabled]="isSaving() || archiveRemarks.trim().length < 5" class="h-10 px-5 rounded-xl bg-slate-800 text-white text-sm font-bold disabled:opacity-50">
                @if (isSaving()) { Archiving... } @else { Archive Project }
              </button>
            </div>
          </div>
        </div>
      }

      @if (showSkipDeclarationModal()) {
        <div class="fixed inset-0 z-[110] bg-slate-900/55 backdrop-blur-sm flex items-center justify-center p-4" (click)="closeSkipDeclarationModal()">
          <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-100 overflow-hidden" (click)="$event.stopPropagation()">
            <div class="p-6 text-center">
              <div class="w-14 h-14 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mx-auto">
                <i class="ti ti-file-alert text-2xl"></i>
              </div>
              <h2 class="text-lg font-bold text-slate-950 mt-4">Skip customer declaration?</h2>
              <p class="text-sm text-slate-500 mt-2">This records the declaration as skipped and unlocks First Fix for this project.</p>
              <div class="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left">
                <p class="text-xs font-semibold text-amber-900">Only continue when the office has approved proceeding without the customer declaration.</p>
              </div>
            </div>
            <div class="px-6 py-4 border-t border-slate-100 bg-slate-50 flex gap-3">
              <button type="button" (click)="closeSkipDeclarationModal()" [disabled]="isSkippingDeclaration()" class="h-11 flex-1 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-bold hover:bg-slate-100 disabled:opacity-50">Cancel</button>
              <button type="button" (click)="confirmSkipDeclaration()" [disabled]="isSkippingDeclaration()" class="h-11 flex-1 rounded-xl bg-amber-600 text-white text-sm font-bold hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-2">
                @if (isSkippingDeclaration()) { <i class="ti ti-loader-2 animate-spin"></i> Skipping... } @else { <i class="ti ti-player-skip-forward-filled"></i> Skip Declaration }
              </button>
            </div>
          </div>
        </div>
      }

    </div>
  `
})
export class AdminProjectDetailComponent implements OnInit {
  project = signal<any | null>(null);
  isLoading = signal(false);
  isUploading = signal(false);
  projectPhotos = signal<any[]>([]);
  isLoadingProjectPhotos = signal(false);
  projectPhotoViewerIndex = signal<number | null>(null);
  downloadingProjectPhotoId = signal<number | null>(null);
  projectCertTypes = [
    { type: 'PS3_Plumbing', label: 'PS3 Plumbing' },
    { type: 'PS3_Drainage', label: 'PS3 Drainage' },
    { type: 'Gas_Safety', label: 'Gas Safety' }
  ];

  // Multi-Worker Management
  showManageWorkers = signal(false);
  allWorkers = signal<any[]>([]);
  fleetVehicles = signal<any[]>([]);
  selectedWorkerIds = signal<number[]>([]);
  selectedFleetVehicleIds: Record<number, string> = {};
  isSaving = signal(false);
  projectWorkAssignments = signal<any[]>([]);
  editingWorkAssignmentId = signal<number | null>(null);
  isWorkAssignmentSaving = signal(false);
  workAssignmentForm = {
    user_id: '',
    start_date: '',
    end_date: '',
    instruction: '',
    status: 'Scheduled',
    fleet_vehicle_id: ''
  };

  // Form Management
  showPhaseFormModal = signal(false);
  activePhaseId = signal<number | null>(null);
  activePhaseName = signal<string>('');
  showPhaseReviewModal = signal(false);
  reviewPhase = signal<any | null>(null);
  phaseReviewMode = signal<'approve' | 'changes'>('approve');
  phaseReviewNotes = '';
  isReviewSaving = signal(false);
  showSafetyModal = signal(false);
  showClientModal = signal(false);
  showCompleteModal = signal(false);
  showArchiveModal = signal(false);
  showSkipDeclarationModal = signal(false);
  isSkippingDeclaration = signal(false);
  archiveRemarks = '';

  // First Fix Full Schema
  formTechDetails = { fullName: '', phone: '', plumbingLicence: '', drainageLicence: '', gasLicence: '' };
  formSelections: any = {
    pipework: {},
    hydraulicInst: {},
    hydraulicSetout: {},
    hydraulicTest: {},
    drainageInst: {},
    drainageFind: {}
  };
  formNotes = '';
  formSignature = '';
  formSignatureDate = '';

  pipeworkOptions = [
    'Kempex Slide', 'Fusiotherm', 'Cast Iron', 'uPVC', 'PVC(D)', 'Copper',
    'Galvanised', 'Alkathene', 'Pex pipe', 'HDPE', 'MDPE', 'None',
    'Earthenware', 'Kempex Gas', 'Dux Bute'
  ];

  hydraulicInstOptions = [
    'Specified waste pipe work is acoustic lagged', 'Penetrations through building paper are trade sealed',
    'Penetrations through fire cells are fire protected', 'Vent penetration has been back flashed and sealed',
    'All Joist Brackets or Reinforcement installed if required', 'All exposed pipe work is correctly labeled',
    "Nog's and fixings installed for WC,WHB,SHA,HWC?", 'Hot Water Ring main is thermally lagged',
    'All pipe work clipped neatly and tidy', 'Waterman connected to water meter & building',
    'Water main Supply is under 500kpa', 'All pipework secured/siliconed',
    'Pre-line pluming inspection booked', 'FWG flange recessed into flooring and installed',
    'None'
  ];

  hydraulicSetoutOptions = [
    'Pipework run to Current Construction set drawings', 'Copies of tape & fittings schedule supplied',
    'Copies of tape & fittings set out schedule supplied', 'Checked Soil and waste pipes for minimum fall',
    'Discharge units calculated for fixtures waste system', 'Hot water supply lines run the shortest',
    'Fittings and Fixtures confirmed & on order?', 'Hydraulics checked vs Architectural drawings',
    'None'
  ];

  hydraulicTestOptions = [
    'Water tested to 1500kpa and loop not affect lining', 'Checked for drips on water and waste piping',
    'Picked up all left over material and removed from site', 'Soil and waste piping hydrostatic test (Flood Test)',
    'Backflow certification complete/booked', 'Test plug & Gauge removed from pipe system',
    'As built completed and handed in', 'Fire penetration Schedule completed',
    'Gas pipe work tested to twice the working pressure', 'None'
  ];

  drainageInstOptions = [
    'Pipe layout setout match the architecture plan', 'Drainage inspection booked (All pipe work have minimum fall)',
    'All pipework have minimum cover according to AS/NZS 3500.2', 'FWG & ORG is concrete supported',
    'SS & SW pipe connected to public line', 'All pipe work tested with water tester airtest',
    'Under floor drainage as built completed', 'Rehau pit SW. underground tank installed',
    'All left over mud removed from site', 'None'
  ];

  drainageFindOptions = [
    'Above ground tank installed', 'Orifice size checked',
    'Half siphon cesspit installed', 'All cesspit and tank cover secure and safe',
    'Org hatching Complete', 'Drainage PS3 Complete',
    'None'
  ];

  // Second Fix Schema Definitions
  hydraulicSetoutSecondFixOptions = [
    'Layout set to current construction set drawings', 'Water pipe work maintained test throughout', 'Fit-off Materials match schedule on plans?',
    'Set-outs/minimum clearances as per plan', 'Taps are suitable for Equal System', 'No debris in floor wastes or stubs',
    'Hydraulics checked vs Architectural drawings', 'Walls and floor coverings complete for install', 'None'
  ];

  tapwareFitoffOptions = [
    'Pressure limiting valve installed on main supply', 'Hose taps are complete with Vacuum Breakers', 'HWC relief lines have 25mm clearance',
    'HWC restraints installed', 'HWC Tempered to required outlet temp', 'Backflow valves installed with clearances & certified',
    'Flanges installed on all pipe work and vermin proofed', 'All soil pipes connected to sanitary sewer', 'No crossovers, hot on left and cold on right',
    'All fixtures are silicon-ed down and splash sealed', 'Gas pipe work tested and cert complete', 'Shower hoses fitted with non returns (if required)',
    'FWG flange recessed into flooring and installed', 'All down pipe installed and sealed', 'SW pump system installed', 'non portable sign installed if required', 'None'
  ];

  gasWaterHeaterOptions = [
    'Water tested to 1500kpa and loop not affect lining', 'Checked for drips on water and waste piping', 'Picked up all left over material and removed from site',
    'Soil and waste piping hydrostatic test (Flood Test)', 'Backflow certification complete/booked', 'Test plug & Gauge removed from pipe system',
    'As built completed and handed in', 'Fire penetration Schedule completed', 'Gas pipe work tested to twice the working pressure',
    'Commision all gas appliances', 'None'
  ];

  // Canvas Signature Properties (First/Second Fix)
  @ViewChild('signaturePad') signaturePad!: ElementRef<HTMLCanvasElement>;
  isDrawing = false;
  signatureEmpty = true;
  lastCanvasPos = { x: 0, y: 0 };

  // Canvas Signature Properties (Compliance Certificates)
  @ViewChild('certSignaturePad1') certSignaturePad1!: ElementRef<HTMLCanvasElement>;
  @ViewChild('certSignaturePad2') certSignaturePad2!: ElementRef<HTMLCanvasElement>;
  certSignatureEmpty1 = true;
  certSignatureEmpty2 = true;
  isDrawingCert1 = false;
  isDrawingCert2 = false;
  lastCanvasPosCert1 = { x: 0, y: 0 };
  lastCanvasPosCert2 = { x: 0, y: 0 };

  // Official Compliance Certificates Models Tracker
  showCertModal = signal(false);
  activeCertType = signal<'PS3_Plumbing' | 'PS3_Drainage' | 'Gas_Safety' | null>(null);

  certFormPlumbing = {
    authorName: CERTIFICATE_PROFILE.name, authorCompany: CERTIFICATE_PROFILE.company, buildingConsentNo: '', description: '',
    legalDescription: '', siteAddress: '',
    tests: { g12: false, as3500_1: false, as3500_4: false, as3500_5: false, as2032: false, manufacturers: false },
    testReportAttached: 'No', // Can be 'Yes' or 'No'
    signature: '', date: '', registrationNo: CERTIFICATE_PROFILE.registrationNo,
    contact: defaultCertificateContact()
  };

  certFormDrainage = {
    authorName: CERTIFICATE_PROFILE.name, authorCompany: CERTIFICATE_PROFILE.company, buildingConsentNo: '', description: '',
    legalDescription: '', siteAddress: '',
    tests: { vm1_water: false, vm1_air: false, as3500_2_water: false, as3500_2_air: false, as2032: false },
    signature: '', date: '', registrationNo: CERTIFICATE_PROFILE.registrationNo,
    contact: defaultCertificateContact()
  };

  certFormGas = {
    clientName: '', jobRef: '', icp: '', address: '', suburb: '', city: '', description: '',
    gasPressure: '', riskClass: 'General', gasType: 'Natural gas', gasTypeOther: '',
    certifiedDesign: 'No', certifiedDesignIdentify: '', certifiedDesignLink: '',
    manufacturerInst: 'No', manufacturerInstIdentify: '', manufacturerInstLink: '',
    complianceMeans: 'AS/NZS 5601.1', complianceOther: 'No', complianceOtherSpec: '',
    partsSafe: 'All', partsSafeSpec: '',
    workDates: '', supervisionNameReg: '',
    certifierName: CERTIFICATE_PROFILE.name, certifierReg: CERTIFICATE_PROFILE.registrationNo, signature1: '', date1: '',
    authPersonName: CERTIFICATE_PROFILE.name, authPersonReg: CERTIFICATE_PROFILE.registrationNo, connectionDate: '', signature2: '', date2: ''
  };

  getCertStatus(type: string): string {
    const certs = this.project()?.certificates;
    if (certs && certs[type]) {
      return 'Completed';
    }
    return 'Pending';
  }

  isCorePhase(phase: any): boolean {
    return ['First-Fix', 'Second-Fix'].includes(phase?.phase_name);
  }

  hasPhasePdf(phase: any): boolean {
    const readyStatuses = ['Submitted', 'Approved', 'Completed'];
    return this.isCorePhase(phase) && (!!phase?.form_data || readyStatuses.includes(phase?.status));
  }

  hasAnyPhasePdf(): boolean {
    return (this.project()?.phases || []).some((phase: any) => this.hasPhasePdf(phase));
  }

  activePhaseData(): any | null {
    const id = this.activePhaseId();
    if (!id) return null;
    return (this.project()?.phases || []).find((phase: any) => Number(phase.id) === Number(id)) || null;
  }

  downloadPhasePdf(phase: any) {
    if (!phase?.id) return;
    if (phase.phase_name === 'First-Fix') {
      this.downloadFirstFixPdf(phase.id);
      return;
    }
    if (phase.phase_name === 'Second-Fix') {
      this.downloadSecondFixPdf(phase.id);
    }
  }

  openCertForm(type: 'PS3_Plumbing' | 'PS3_Drainage' | 'Gas_Safety') {
    if (this.isArchived()) {
      showToast('error', 'Archived projects cannot be changed.');
      return;
    }
    this.activeCertType.set(type);
    this.certSignatureEmpty1 = true;
    this.certSignatureEmpty2 = true;

    const existing = this.project()?.certificates?.[type]?.form_data;
    if (existing) {
      if (type === 'PS3_Plumbing') this.certFormPlumbing = { ...this.certFormPlumbing, ...existing };
      if (type === 'PS3_Drainage') this.certFormDrainage = { ...this.certFormDrainage, ...existing };
      if (type === 'Gas_Safety') this.certFormGas = { ...this.certFormGas, ...existing };

      if (existing.signature) this.certSignatureEmpty1 = false;
      if (existing.signature1) this.certSignatureEmpty1 = false;
      if (existing.signature2) this.certSignatureEmpty2 = false;
    } else {
      const today = new Date().toISOString().split('T')[0];
      if (type === 'PS3_Plumbing') {
        this.certFormPlumbing = {
          authorName: CERTIFICATE_PROFILE.name, authorCompany: CERTIFICATE_PROFILE.company, buildingConsentNo: '', description: '',
          legalDescription: '', siteAddress: '',
          tests: { g12: false, as3500_1: false, as3500_4: false, as3500_5: false, as2032: false, manufacturers: false },
          testReportAttached: 'No', signature: '', date: today, registrationNo: CERTIFICATE_PROFILE.registrationNo,
          contact: defaultCertificateContact()
        };
      } else if (type === 'PS3_Drainage') {
        this.certFormDrainage = {
          authorName: CERTIFICATE_PROFILE.name, authorCompany: CERTIFICATE_PROFILE.company, buildingConsentNo: '', description: '',
          legalDescription: '', siteAddress: '',
          tests: { vm1_water: false, vm1_air: false, as3500_2_water: false, as3500_2_air: false, as2032: false },
          signature: '', date: today, registrationNo: CERTIFICATE_PROFILE.registrationNo,
          contact: defaultCertificateContact()
        };
      } else if (type === 'Gas_Safety') {
        this.certFormGas = {
          clientName: '', jobRef: '', icp: '', address: '', suburb: '', city: '', description: '',
          gasPressure: '', riskClass: 'General', gasType: 'Natural gas', gasTypeOther: '',
          certifiedDesign: 'No', certifiedDesignIdentify: '', certifiedDesignLink: '',
          manufacturerInst: 'No', manufacturerInstIdentify: '', manufacturerInstLink: '',
          complianceMeans: 'AS/NZS 5601.1', complianceOther: 'No', complianceOtherSpec: '',
          partsSafe: 'All', partsSafeSpec: '',
          workDates: '', supervisionNameReg: '',
          certifierName: CERTIFICATE_PROFILE.name, certifierReg: CERTIFICATE_PROFILE.registrationNo, signature1: '', date1: today,
          authPersonName: CERTIFICATE_PROFILE.name, authPersonReg: CERTIFICATE_PROFILE.registrationNo, connectionDate: today, signature2: '', date2: today
        };
      }
    }

    this.showCertModal.set(true);
    // Re-initialize canvas slightly after render
    setTimeout(() => { this.initCertSignatureCanvas(); }, 100);
  }

  activeCertificateData(): any {
    if (this.activeCertType() === 'PS3_Plumbing') return this.certFormPlumbing;
    if (this.activeCertType() === 'PS3_Drainage') return this.certFormDrainage;
    return this.certFormGas;
  }

  saveCertForm() {
    let finalData: any;

    if (this.activeCertType() === 'PS3_Plumbing') {
      finalData = this.certFormPlumbing;
    } else if (this.activeCertType() === 'PS3_Drainage') {
      finalData = this.certFormDrainage;
    } else if (this.activeCertType() === 'Gas_Safety') {
      finalData = this.certFormGas;
    }

    const payload = {
      project_id: this.project().id,
      cert_type: this.activeCertType(),
      form_data: finalData
    };

    this.api.saveProjectCertificate(payload).subscribe({
      next: (res: any) => {
        if (res.success) {
          showToast('success', `${this.activeCertType()?.replace('_', ' ')} saved securely`);
          this.showCertModal.set(false);
          this.loadProject(this.project().id); // Refresh grid
        } else {
          showToast('error', res.message || 'Save failed');
        }
      },
      error: () => showToast('error', 'Network error tracking certificate')
    });
  }

  downloadCertPdf(type: string) {
    const docMap: Record<string, string> = {
      'PS3_Plumbing': 'ps3-plumbing',
      'PS3_Drainage': 'ps3-drainage',
      'Gas_Safety': 'gas-certificate'
    };
    this.openProjectPdf(docMap[type], this.project().id);
  }

  // --- COMPLIANCE SIGNATURE CANVAS PIPELINE ---
  initCertSignatureCanvas() {
    setTimeout(() => {
      const pads = [this.certSignaturePad1, this.certSignaturePad2];
      pads.forEach(pad => {
        if (!pad?.nativeElement) return;
        const canvas = pad.nativeElement;
        const rect = canvas.getBoundingClientRect();
        const width = Math.max(Math.round(rect.width || canvas.parentElement?.clientWidth || 400), 1);
        const height = Math.max(Math.round(rect.height || canvas.parentElement?.clientHeight || 80), 1);
        if (canvas.width !== width || canvas.height !== height) {
          canvas.width = width;
          canvas.height = height;
        }
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.lineWidth = 2.5;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.strokeStyle = '#020617';
        }
      });
    }, 100);
  }

  startDrawingCert(event: MouseEvent | TouchEvent, padIndex: number) {
    event.preventDefault();
    if (padIndex === 1) this.isDrawingCert1 = true;
    if (padIndex === 2) this.isDrawingCert2 = true;
    this.updateCertCanvasPosition(event, padIndex);
  }

  stopDrawingCert(padIndex: number) {
    if (padIndex === 1) this.isDrawingCert1 = false;
    if (padIndex === 2) this.isDrawingCert2 = false;
  }

  drawCert(event: MouseEvent | TouchEvent, padIndex: number) {
    if (padIndex === 1 && !this.isDrawingCert1) return;
    if (padIndex === 2 && !this.isDrawingCert2) return;

    const canvas = padIndex === 1 ? this.certSignaturePad1?.nativeElement : this.certSignaturePad2?.nativeElement;
    if (!canvas) return;

    event.preventDefault();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in event ? event.touches[0].clientX : (event as MouseEvent).clientX;
    const clientY = 'touches' in event ? event.touches[0].clientY : (event as MouseEvent).clientY;

    const x = clientX - rect.left;
    const y = clientY - rect.top;

    ctx.beginPath();
    if (padIndex === 1) {
      ctx.moveTo(this.lastCanvasPosCert1.x, this.lastCanvasPosCert1.y);
      ctx.lineTo(x, y);
      ctx.stroke();
      this.lastCanvasPosCert1 = { x, y };
      this.certSignatureEmpty1 = false;
    } else {
      ctx.moveTo(this.lastCanvasPosCert2.x, this.lastCanvasPosCert2.y);
      ctx.lineTo(x, y);
      ctx.stroke();
      this.lastCanvasPosCert2 = { x, y };
      this.certSignatureEmpty2 = false;
    }
  }

  updateCertCanvasPosition(event: MouseEvent | TouchEvent, padIndex: number) {
    const canvas = padIndex === 1 ? this.certSignaturePad1?.nativeElement : this.certSignaturePad2?.nativeElement;
    if (!canvas) return;

    event.preventDefault();
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in event ? event.touches[0].clientX : (event as MouseEvent).clientX;
    const clientY = 'touches' in event ? event.touches[0].clientY : (event as MouseEvent).clientY;

    if (padIndex === 1) {
      this.lastCanvasPosCert1 = { x: clientX - rect.left, y: clientY - rect.top };
    } else {
      this.lastCanvasPosCert2 = { x: clientX - rect.left, y: clientY - rect.top };
    }
  }

  clearCertSignature(padIndex: number) {
    const canvas = padIndex === 1 ? this.certSignaturePad1?.nativeElement : this.certSignaturePad2?.nativeElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    if (padIndex === 1) {
      this.certSignatureEmpty1 = true;
      if (this.activeCertType() === 'PS3_Plumbing') this.certFormPlumbing.signature = '';
      if (this.activeCertType() === 'PS3_Drainage') this.certFormDrainage.signature = '';
      if (this.activeCertType() === 'Gas_Safety') this.certFormGas.signature1 = '';
    }
    if (padIndex === 2) {
      this.certSignatureEmpty2 = true;
      this.certFormGas.signature2 = '';
    }
  }

  api = inject(ApiService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  activeTab = signal<'phases' | 'documents' | 'forms' | 'worklog' | 'photos' | 'timeline'>('phases');
  declarationStatus = signal<'Unsigned' | 'Sent' | 'Signed' | 'Skipped'>('Unsigned');
  declarationData = signal<any>(null);
  generatedPreviewDate = new Date();
  isSendingEmail = signal(false);
  isSendingSafetyEmail = signal(false);
  isResolvingSafety = signal(false);

  // Edit Modal
  showEditModal = signal(false);
  editForm = { name: '', address: '', status: '', estimated_end_date: '', description: '' };
  isSavingEdit = signal(false);

  projectId = '';
  private requestedCertType: 'PS3_Plumbing' | 'PS3_Drainage' | 'Gas_Safety' | null = null;
  private requestedCertOpened = false;


  ngOnInit() {
    this.route.queryParamMap.subscribe(params => {
      const requested = params.get('form');
      this.requestedCertType = ['PS3_Plumbing', 'PS3_Drainage', 'Gas_Safety'].includes(requested || '')
        ? requested as 'PS3_Plumbing' | 'PS3_Drainage' | 'Gas_Safety'
        : null;
      if (params.get('tab') === 'forms' || this.requestedCertType) this.activeTab.set('forms');
      this.maybeOpenRequestedCertificate();
    });
    this.route.params.subscribe(params => {
      const id = params['id'];
      this.projectId = id;
      this.requestedCertOpened = false;
      if (id) {
        this.loadProject(id);
        this.loadProjectPhotos(id);
      }
    });
    this.loadAllWorkers();
    this.loadFleetVehicles();
  }

  latestSafetyStatus(): 'Accepted' | 'Declined' | '' {
    return this.project()?.latest_safety_acknowledgement?.status || (this.project()?.latest_safety_acknowledgement ? 'Accepted' : '');
  }

  isArchived(): boolean {
    return Number(this.project()?.is_archived || 0) === 1;
  }

  // ── Phase lock helpers ──
  isFirstFixDone(): boolean {
    const phases = this.project()?.phases || [];
    const ff = phases.find((p: any) => p.phase_name === 'First-Fix');
    return ['Approved', 'Completed'].includes(ff?.status);
  }

  isSecondFixDone(): boolean {
    const phases = this.project()?.phases || [];
    const sf = phases.find((p: any) => p.phase_name === 'Second-Fix');
    return ['Approved', 'Completed'].includes(sf?.status);
  }

  canProgressProject(): boolean {
    if (this.isArchived()) return false;
    return ['Signed', 'Skipped'].includes(this.declarationStatus());
  }

  openSafetyModal() {
    this.showSafetyModal.set(true);
  }

  closeSafetyModal() {
    this.showSafetyModal.set(false);
  }

  sendSafetyDeclineEmail() {
    const ack = this.project()?.latest_safety_acknowledgement;
    if (!ack?.id || this.isSendingSafetyEmail()) return;

    this.isSendingSafetyEmail.set(true);
    this.api.sendSafetyDeclineEmail(ack.id).subscribe({
      next: (res: any) => {
        this.isSendingSafetyEmail.set(false);
        if (res.success) {
          showToast('success', 'Safety concern emailed to customer.');
          this.loadProject(this.projectId);
        } else {
          showToast('error', res.message || 'Email could not be sent.');
        }
      },
      error: (err: any) => {
        this.isSendingSafetyEmail.set(false);
        showToast('error', err?.error?.message || 'Email could not be sent.');
      }
    });
  }

  resolveSafetyConcern() {
    const ack = this.project()?.latest_safety_acknowledgement;
    if (!ack?.id || ack.resolved_at || this.isResolvingSafety()) return;

    const notes = window.prompt('Resolution notes for this safety concern:', '');
    if (notes === null) return;

    this.isResolvingSafety.set(true);
    this.api.resolveSafetyConcern({ acknowledgement_id: ack.id, resolution_notes: notes.trim() }).subscribe({
      next: (res: any) => {
        this.isResolvingSafety.set(false);
        if (res.success) {
          showToast('success', 'Safety concern marked as resolved.');
          this.loadProject(this.projectId);
        } else {
          showToast('error', res.message || 'Safety concern could not be resolved.');
        }
      },
      error: (err: any) => {
        this.isResolvingSafety.set(false);
        showToast('error', err?.error?.message || 'Safety concern could not be resolved.');
      }
    });
  }

  openClientModal() {
    this.showClientModal.set(true);
  }

  closeClientModal() {
    this.showClientModal.set(false);
  }

  projectContacts(): any[] {
    return this.project()?.contacts || [];
  }

  totalPhaseCount(): number {
    return (this.project()?.phases || []).length;
  }

  approvedPhaseCount(): number {
    return (this.project()?.phases || []).filter((p: any) => ['Approved', 'Completed'].includes(p.status)).length;
  }

  submittedPhaseCount(): number {
    return (this.project()?.phases || []).filter((p: any) => p.status === 'Submitted').length;
  }

  loggedHours(): string {
    const project = this.project();
    if (!project) return '0.00';
    if (project.combined_logged_hours !== null && project.combined_logged_hours !== undefined) {
      return Number(project.combined_logged_hours || 0).toFixed(2);
    }
    const manualHours = Number(project.total_logged_hours || 0);
    const sessionSeconds = project.total_work_session_seconds !== null && project.total_work_session_seconds !== undefined
      ? Number(project.total_work_session_seconds || 0)
      : (project.work_sessions || []).reduce((sum: number, session: any) => sum + Number(session.total_seconds || 0), 0);
    return (manualHours + (sessionSeconds / 3600)).toFixed(2);
  }

  workLogRows(): any[] {
    const project = this.project();
    if (!project) return [];
    const sessionRows = (project.work_sessions || []).map((session: any) => ({
      ...session,
      key: `session-${session.id}`,
      source: 'timer',
      duration_hours: Number(session.current_total_seconds ?? session.total_seconds ?? 0) / 3600
    }));
    const manualRows = (project.manual_hours || []).map((entry: any) => ({
      ...entry,
      key: `manual-${entry.id}`,
      source: 'manual',
      started_at: null,
      stopped_at: null,
      status: 'logged',
      duration_hours: Number(entry.hours_logged || 0)
    }));
    return [...sessionRows, ...manualRows].sort((a, b) => this.workLogSortTime(b) - this.workLogSortTime(a));
  }

  private workLogSortTime(row: any): number {
    if (row.source === 'manual' && row.work_date) {
      const parts = String(row.work_date).split('-').map(Number);
      if (parts.length === 3) return Date.UTC(parts[0], parts[1] - 1, parts[2]);
    }
    const value = new Date(row.started_at || row.created_at || 0).getTime();
    return Number.isNaN(value) ? 0 : value;
  }

  formatWorkLogStarted(row: any): string {
    if (row.source === 'manual') {
      const parts = String(row.work_date || '').split('-').map(Number);
      if (parts.length !== 3 || parts.some(part => !Number.isFinite(part))) return 'Date unavailable';
      return new Intl.DateTimeFormat('en-NZ', {
        timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric'
      }).format(new Date(Date.UTC(parts[0], parts[1] - 1, parts[2])));
    }
    return this.formatAucklandDateTime(row.started_at);
  }

  formatAucklandDateTime(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Date unavailable';
    return new Intl.DateTimeFormat('en-NZ', {
      timeZone: 'Pacific/Auckland', day: 'numeric', month: 'short', year: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
    }).format(date);
  }

  financialBalanceDue(): number {
    return Number(this.project()?.linked_documents?.financial_summary?.balance_due_total || 0);
  }

  completionChecklist(): any[] {
    const project = this.project();
    const certs = project?.certificates || {};
    const asBuiltCount = project?.linked_documents?.as_built?.length || 0;
    return [
      {
        label: 'Customer declaration signed or skipped',
        done: this.canProgressProject(),
        detail: this.canProgressProject() ? `Declaration is ${this.declarationStatus().toLowerCase()}.` : 'Customer declaration is still pending.'
      },
      {
        label: 'Second Fix approved',
        done: this.isSecondFixDone(),
        detail: this.isSecondFixDone() ? 'Second Fix has been approved or completed.' : 'Second Fix must be approved before completion.'
      },
      {
        label: 'Project certificates reviewed',
        done: !!(certs.PS3_Plumbing || certs.PS3_Drainage || certs.Gas_Safety),
        detail: certs.PS3_Plumbing || certs.PS3_Drainage || certs.Gas_Safety ? 'At least one certificate is completed.' : 'No project certificates are completed yet.'
      },
      {
        label: 'As-built records attached',
        done: asBuiltCount > 0,
        detail: asBuiltCount > 0 ? `${asBuiltCount} as-built record(s) attached.` : 'No as-built record is attached.'
      },
      {
        label: 'Commercial balance reviewed',
        done: this.financialBalanceDue() <= 0,
        detail: this.financialBalanceDue() <= 0 ? 'No project balance due.' : `Balance due: ${this.financialBalanceDue().toLocaleString('en-NZ', { style: 'currency', currency: 'NZD' })}.`
      }
    ];
  }

  projectTimeline(): any[] {
    const project = this.project();
    if (!project) return [];
    const items: any[] = [];
    items.push({
      key: 'created',
      title: 'Project created',
      detail: project.customer_name ? `Created for ${project.customer_name}` : 'Project record created',
      date: project.created_at,
      icon: 'ti-building-skyscraper',
      iconClass: 'bg-slate-50 text-slate-500'
    });
    if (this.declarationData()) {
      items.push({
        key: 'declaration',
        title: `Declaration ${this.declarationStatus().toLowerCase()}`,
        detail: this.declarationData()?.customer_name ? `Customer: ${this.declarationData().customer_name}` : 'Customer declaration updated',
        date: this.declarationData()?.signed_at || this.declarationData()?.created_at,
        icon: this.declarationStatus() === 'Signed' ? 'ti-file-check' : (this.declarationStatus() === 'Skipped' ? 'ti-player-skip-forward' : 'ti-mail-forward'),
        iconClass: this.declarationStatus() === 'Signed' ? 'bg-emerald-50 text-emerald-600' : (this.declarationStatus() === 'Skipped' ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600')
      });
    }
    if (project.is_archived) {
      items.push({
        key: 'archived',
        title: 'Project archived',
        detail: project.archive_remarks || 'Archived without remarks',
        date: project.archived_at,
        icon: 'ti-archive',
        iconClass: 'bg-slate-100 text-slate-600'
      });
    }
    for (const phase of project.phases || []) {
      if (phase.status && phase.status !== 'Pending') {
        items.push({
          key: `phase-${phase.id}-${phase.status}`,
          title: `${phase.phase_name} ${phase.status}`,
          detail: phase.review_notes || 'Phase status updated',
          date: phase.reviewed_at || phase.completed_at || phase.updated_at || project.created_at,
          icon: phase.status === 'Needs Changes' ? 'ti-message-report' : 'ti-clipboard-check',
          iconClass: phase.status === 'Needs Changes' ? 'bg-amber-50 text-amber-600' : 'bg-sky-50 text-sky-600'
        });
      }
    }
    for (const ack of project.safety_acknowledgements || []) {
      items.push({
        key: `safety-${ack.id}`,
        title: 'Safety acknowledgement accepted',
        detail: `Accepted by ${ack.worker_name || 'Worker'}`,
        date: ack.accepted_at,
        icon: 'ti-shield-check',
        iconClass: 'bg-emerald-50 text-emerald-600'
      });
    }
    for (const doc of project.documents || []) {
      items.push({
        key: `doc-${doc.id}`,
        title: 'Document uploaded',
        detail: doc.name || 'Project document',
        date: doc.created_at,
        icon: 'ti-file-upload',
        iconClass: 'bg-slate-50 text-slate-500'
      });
    }
    for (const quote of project.linked_documents?.quotes || []) {
      items.push({
        key: `quote-${quote.id}`,
        title: `Quote ${quote.quote_no}`,
        detail: `${quote.status} - ${Number(quote.total || 0).toLocaleString('en-NZ', { style: 'currency', currency: 'NZD' })}`,
        date: quote.updated_at || quote.created_at,
        icon: 'ti-file-dollar',
        iconClass: 'bg-sky-50 text-sky-600'
      });
    }
    return items.sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
  }

  phaseStatusClass(status: string): string {
    const normalized = status || 'Pending';
    if (normalized === 'Approved' || normalized === 'Completed') return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    if (normalized === 'Submitted') return 'bg-sky-50 text-sky-700 border-sky-100';
    if (normalized === 'Needs Changes') return 'bg-amber-50 text-amber-700 border-amber-100';
    if (normalized === 'In Progress' || normalized === 'Draft Saved') return 'bg-blue-50 text-blue-700 border-blue-100';
    return 'bg-slate-100 text-slate-600 border-slate-200';
  }

  // ── Edit Project Modal ──
  openEditModal() {
    const p = this.project();
    if (!p || this.isArchived()) return;
    this.editForm = {
      name: p.name || '',
      address: p.address || '',
      status: p.status || '',
      estimated_end_date: p.estimated_end_date || '',
      description: p.description || ''
    };
    this.showEditModal.set(true);
  }

  saveEditForm() {
    if (this.isSavingEdit()) return;
    this.isSavingEdit.set(true);
    const payload = { id: this.projectId, ...this.editForm };
    this.api.updateProject(payload).subscribe({
      next: (res: any) => {
        this.isSavingEdit.set(false);
        if (res.success) {
          showToast('success', 'Project updated successfully.');
          this.showEditModal.set(false);
          this.loadProject(this.projectId);
        } else {
          showToast('error', res.message || 'Update failed.');
        }
      },
      error: () => { this.isSavingEdit.set(false); showToast('error', 'Network error.'); }
    });
  }

  // ── Complete Project ──
  completeProject() {
    if (this.isArchived()) {
      showToast('error', 'Archived projects cannot be completed.');
      return;
    }
    if (!this.canProgressProject()) {
      showToast('error', 'Customer declaration must be signed or skipped before completing the project.');
      return;
    }
    if (!this.isSecondFixDone()) {
      showToast('error', 'Second Fix must be Completed before marking project complete.');
      return;
    }
    this.showCompleteModal.set(true);
  }

  confirmCompleteProject() {
    this.api.updateProject({ id: this.projectId, status: 'Completed' }).subscribe({
      next: (res: any) => {
        if (res.success) {
          showToast('success', 'Project marked as Completed!');
          this.showCompleteModal.set(false);
          this.loadProject(this.projectId);
        } else {
          showToast('error', res.message || 'Failed.');
        }
      },
      error: () => showToast('error', 'Network error.')
    });
  }

  openArchiveModal() {
    this.archiveRemarks = '';
    this.showArchiveModal.set(true);
  }

  closeArchiveModal() {
    if (this.isSaving()) return;
    this.showArchiveModal.set(false);
    this.archiveRemarks = '';
  }

  archiveProject() {
    const id = this.project()?.id;
    const remarks = this.archiveRemarks.trim();
    if (!id || this.isSaving()) return;
    if (remarks.length < 5) {
      showToast('error', 'Please add archive remarks before archiving.');
      return;
    }
    this.isSaving.set(true);
    this.api.archiveReference({ reference_id: id, model_type: 'Project', remarks }).subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (res.success) {
          showToast('success', 'Project archived.');
          this.closeArchiveModal();
          this.loadProject(id.toString());
        } else {
          showToast('error', res.message || 'Project could not be archived.');
        }
      },
      error: (err: any) => {
        this.isSaving.set(false);
        showToast('error', err?.error?.message || 'Project could not be archived.');
      }
    });
  }

  loadAllWorkers() {
    this.api.getWorkers().subscribe({
      next: (res: any) => {
        if (res.success && res.data?.users) {
          this.allWorkers.set(res.data.users.filter((u: any) => u.is_active));
        }
      }
    });
  }

  loadFleetVehicles() {
    this.api.getFleetVehicles().subscribe({
      next: (res: any) => {
        if (res.success && res.data?.vehicles) {
          this.fleetVehicles.set(res.data.vehicles.filter((vehicle: any) => vehicle.is_active !== false));
        }
      }
    });
  }

  openManageWorkers() {
    if (this.isArchived()) {
      showToast('error', 'Archived projects are view-only.');
      return;
    }
    const currentWorkers = this.project()?.assigned_workers || [];
    this.selectedWorkerIds.set(currentWorkers.map((w: any) => Number(w.id)));
    this.selectedFleetVehicleIds = {};
    this.projectWorkAssignments.set(this.project()?.work_assignments || []);
    this.resetWorkAssignmentForm();
    this.showManageWorkers.set(true);
  }

  closeManageWorkers() {
    this.showManageWorkers.set(false);
    this.selectedFleetVehicleIds = {};
    this.resetWorkAssignmentForm();
  }

  toggleWorkerSelection(id: number) {
    this.selectedWorkerIds.update(ids => {
      if (ids.includes(id)) {
        delete this.selectedFleetVehicleIds[id];
        return ids.filter(i => i !== id);
      }
      return [...ids, id];
    });
  }

  isWorkerSelected(id: number): boolean {
    return this.selectedWorkerIds().includes(id);
  }

  saveAssignments() {
    const id = this.project()?.id;
    if (!id || this.isSaving()) return;
    if (this.isArchived()) {
      showToast('error', 'Archived projects are view-only.');
      return;
    }

    this.isSaving.set(true);
    const payload = {
      reference_id: id,
      model_type: 'Project',
      user_ids: this.selectedWorkerIds()
    };

    this.api.assignWorker(payload).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.saveOptionalProjectFleetAssignments(id, () => {
            this.isSaving.set(false);
            showToast('success', 'Team assignment updated successfully');
            this.closeManageWorkers();
            this.loadProject(id.toString());
            this.loadFleetVehicles();
          });
        } else {
          this.isSaving.set(false);
          showToast('error', res.message || 'Failed to update team');
        }
      },
      error: () => {
        this.isSaving.set(false);
        showToast('error', 'Network error updating team');
      }
    });
  }

  resetWorkAssignmentForm() {
    const today = new Date().toISOString().split('T')[0];
    this.editingWorkAssignmentId.set(null);
    this.workAssignmentForm = {
      user_id: '',
      start_date: today,
      end_date: today,
      instruction: '',
      status: 'Scheduled',
      fleet_vehicle_id: ''
    };
  }

  workerName(userId: string | number): string {
    const worker = this.allWorkers().find((w: any) => Number(w.id) === Number(userId));
    return worker?.name || 'Plumber';
  }

  fleetVehicleLabel(vehicle: any): string {
    const rego = vehicle?.rego ? `${vehicle.rego} - ` : '';
    const current = vehicle?.name && vehicle.name !== 'Unassigned' ? ` (${vehicle.name})` : '';
    return `${rego}${vehicle?.vehicle || 'Vehicle'}${current}`;
  }

  formatWorkAssignmentRange(item: any): string {
    if (!item?.start_date) return '';
    if (!item.end_date || item.end_date === item.start_date) return item.start_date;
    return `${item.start_date} to ${item.end_date}`;
  }

  editProjectWorkAssignment(item: any) {
    this.editingWorkAssignmentId.set(Number(item.id));
    this.workAssignmentForm = {
      user_id: String(item.user_id || ''),
      start_date: item.start_date || '',
      end_date: item.end_date || item.start_date || '',
      instruction: item.instruction || '',
      status: item.status || 'Scheduled',
      fleet_vehicle_id: ''
    };
  }

  saveProjectWorkAssignment() {
    const projectId = this.project()?.id;
    if (!projectId || this.isWorkAssignmentSaving()) return;
    if (this.isArchived()) {
      showToast('error', 'Archived projects are view-only.');
      return;
    }
    if (!this.workAssignmentForm.user_id || !this.workAssignmentForm.start_date) {
      showToast('error', 'Select a plumber and start date.');
      return;
    }

    this.isWorkAssignmentSaving.set(true);
    const payload = {
      action: this.editingWorkAssignmentId() ? 'update' : 'create',
      id: this.editingWorkAssignmentId(),
      project_id: projectId,
      user_id: Number(this.workAssignmentForm.user_id),
      start_date: this.workAssignmentForm.start_date,
      end_date: this.workAssignmentForm.end_date || this.workAssignmentForm.start_date,
      instruction: this.workAssignmentForm.instruction,
      status: this.workAssignmentForm.status
    };

    this.api.saveProjectWorkAssignment(payload).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.saveSingleProjectFleetAssignment(projectId, payload.user_id, () => {
            this.isWorkAssignmentSaving.set(false);
            showToast('success', this.editingWorkAssignmentId() ? 'Project work schedule updated.' : 'Project work scheduled.');
            this.projectWorkAssignments.set(res.data?.work_assignments || []);
            this.selectedWorkerIds.update(ids => ids.includes(payload.user_id) ? ids : [...ids, payload.user_id]);
            this.resetWorkAssignmentForm();
            this.loadProject(projectId.toString());
            this.loadFleetVehicles();
          });
        } else {
          this.isWorkAssignmentSaving.set(false);
          showToast('error', res.message || 'Could not save project work schedule.');
        }
      },
      error: (err: any) => {
        this.isWorkAssignmentSaving.set(false);
        showToast('error', err?.error?.message || 'Network error saving project work schedule.');
      }
    });
  }

  private saveOptionalProjectFleetAssignments(projectId: string | number, onComplete: () => void) {
    const requests = this.selectedWorkerIds()
      .map(workerId => ({
        workerId,
        vehicleId: Number(this.selectedFleetVehicleIds[workerId] || 0)
      }))
      .filter(item => item.vehicleId > 0)
      .map(item => this.api.assignFleetVehicle({
        id: item.vehicleId,
        assigned_user_id: item.workerId,
        name: this.workerName(item.workerId),
        status: 'Active on Job',
        job: `Project ${projectId}`,
        location: this.project()?.address || 'Field',
        notes: `Assigned during project team update ${projectId}`
      }));

    if (requests.length === 0) {
      onComplete();
      return;
    }

    forkJoin(requests).subscribe({
      next: () => onComplete(),
      error: (err: any) => {
        this.isSaving.set(false);
        showToast('error', err?.error?.message || 'Project team updated, but fleet assignment failed.');
      }
    });
  }

  private saveSingleProjectFleetAssignment(projectId: string | number, workerId: number, onComplete: () => void) {
    const vehicleId = Number(this.workAssignmentForm.fleet_vehicle_id || 0);
    if (vehicleId <= 0) {
      onComplete();
      return;
    }

    this.api.assignFleetVehicle({
      id: vehicleId,
      assigned_user_id: workerId,
      name: this.workerName(workerId),
      status: this.workAssignmentForm.status === 'Completed' || this.workAssignmentForm.status === 'Cancelled' ? 'Available' : 'Active on Job',
      job: `Project ${projectId}`,
      location: this.project()?.address || 'Field',
      notes: `Assigned during project work schedule ${projectId}`
    }).subscribe({
      next: () => onComplete(),
      error: (err: any) => {
        this.isWorkAssignmentSaving.set(false);
        showToast('error', err?.error?.message || 'Project work saved, but fleet assignment failed.');
      }
    });
  }

  deleteProjectWorkAssignment(item: any) {
    if (!item?.id || this.isWorkAssignmentSaving()) return;
    if (!confirm('Remove this project work schedule?')) return;
    this.isWorkAssignmentSaving.set(true);
    this.api.deleteProjectWorkAssignment(Number(item.id)).subscribe({
      next: (res: any) => {
        this.isWorkAssignmentSaving.set(false);
        if (res.success) {
          showToast('success', 'Project work schedule removed.');
          this.projectWorkAssignments.update(items => items.filter((i: any) => Number(i.id) !== Number(item.id)));
          this.loadProject(this.project()?.id?.toString() || '');
        } else {
          showToast('error', res.message || 'Could not remove project work schedule.');
        }
      },
      error: (err: any) => {
        this.isWorkAssignmentSaving.set(false);
        showToast('error', err?.error?.message || 'Network error removing project work schedule.');
      }
    });
  }

  // Phase Form Methods
  startPhase(phase: any) {
    if (!phase?.id) return;
    if (phase.status !== 'Pending') {
      showToast('error', 'Only pending phases can be started manually.');
      return;
    }
    this.updatePhaseStatus(phase.id, 'In Progress');
  }

  updatePhaseStatus(phaseId: number, status: string, reviewNotes = '') {
    const phaseToUpdate = this.project()?.phases?.find((p: any) => p.id === phaseId);
    if (!phaseToUpdate) return;
    if (this.isArchived()) {
      showToast('error', 'Archived projects are view-only.');
      return;
    }

    if (status === 'Needs Changes' && !reviewNotes.trim()) {
      this.openPhaseReview(phaseToUpdate, 'changes');
      setTimeout(() => this.project.set({ ...this.project() }), 50);
      return;
    }

    if (status === 'Approved' && !reviewNotes.trim() && phaseToUpdate.status === 'Submitted') {
      this.openPhaseReview(phaseToUpdate, 'approve');
      setTimeout(() => this.project.set({ ...this.project() }), 50);
      return;
    }

    if (phaseToUpdate.phase_name === 'Second-Fix' && ['In Progress', 'Submitted', 'Needs Changes', 'Approved', 'Completed'].includes(status)) {
      const firstFix = this.project()?.phases?.find((p: any) => p.phase_name === 'First-Fix');
      if (firstFix && !['Approved', 'Completed'].includes(firstFix.status)) {
        showToast('error', 'First Fix must be approved before progressing Second Fix.');
        setTimeout(() => this.project.set({ ...this.project() }), 50); // Reset visual DOM safely
        return;
      }
    }

    // Local Optimistic Update
    const originalStatus = phaseToUpdate.status;
    phaseToUpdate.status = status;

    const payload = {
      project_id: this.project()?.id,
      phase_id: phaseId,
      status: status,
      review_notes: reviewNotes
    };

    this.api.updateProjectPhase(payload).subscribe({
      next: (res: any) => {
        if (res.success) {
          const feedback = res?.data?.feedback_request;
          if (feedback?.enabled && feedback.email_sent) {
            showToast('success', status === 'Completed' ? 'Phase completed and feedback request sent.' : 'Phase status updated to ' + status);
          } else if (feedback?.enabled && !feedback.email_sent) {
            showToast('error', feedback.message || 'Phase status updated. Feedback request was not sent.');
          } else {
            showToast('success', 'Phase status updated to ' + status);
          }
        } else {
          showToast('error', res.message || 'Failed to update phase status.');
          phaseToUpdate.status = originalStatus; // Revert
          this.project.set({ ...this.project() });
        }
      },
      error: () => {
        showToast('error', 'Network error syncing phase status.');
        phaseToUpdate.status = originalStatus; // Revert
        this.project.set({ ...this.project() });
      }
    });
  }

  approvePhase(phaseId: number) {
    const phase = this.project()?.phases?.find((p: any) => p.id === phaseId);
    if (phase) this.openPhaseReview(phase, 'approve');
  }

  requestPhaseChanges(phaseId: number) {
    const phase = this.project()?.phases?.find((p: any) => p.id === phaseId);
    if (phase) this.openPhaseReview(phase, 'changes');
  }

  openPhaseReview(phase: any, mode: 'approve' | 'changes') {
    this.reviewPhase.set(phase);
    this.phaseReviewMode.set(mode);
    this.phaseReviewNotes = mode === 'changes' ? (phase.review_notes || '') : '';
    this.showPhaseReviewModal.set(true);
  }

  closePhaseReview() {
    if (this.isReviewSaving()) return;
    this.showPhaseReviewModal.set(false);
    this.reviewPhase.set(null);
    this.phaseReviewNotes = '';
  }

  reviewPhaseHasSignature(): boolean {
    return !!(this.reviewPhase()?.form_data?.signature);
  }

  submitPhaseReview(status: 'Approved' | 'Needs Changes') {
    const phase = this.reviewPhase();
    if (!phase || this.isReviewSaving()) return;
    const notes = this.phaseReviewNotes.trim();
    if (status === 'Needs Changes' && !notes) {
      showToast('error', 'Please add a short review note for the field worker.');
      return;
    }

    this.isReviewSaving.set(true);
    this.api.updateProjectPhase({
      project_id: this.project()?.id,
      phase_id: phase.id,
      status,
      review_notes: notes
    }).subscribe({
      next: (res: any) => {
        this.isReviewSaving.set(false);
        if (res.success) {
          showToast('success', status === 'Approved' ? 'Phase approved.' : 'Changes requested from field worker.');
          this.closePhaseReview();
          this.loadProject(this.projectId);
        } else {
          showToast('error', res.message || 'Failed to save review.');
        }
      },
      error: () => {
        this.isReviewSaving.set(false);
        showToast('error', 'Network error saving review.');
      }
    });
  }

  openPhaseForm(phase: any) {
    if (this.isArchived()) {
      showToast('error', 'Archived projects are view-only.');
      return;
    }
    if (phase.phase_name === 'Second-Fix') {
      const firstFix = this.project()?.phases?.find((p: any) => p.phase_name === 'First-Fix');
      if (firstFix && !['Approved', 'Completed'].includes(firstFix.status)) {
        showToast('error', 'First Fix must be approved before initiating the Second Fix phase workflow.');
        return;
      }
    }

    this.activePhaseId.set(phase.id);
    this.activePhaseName.set(phase.phase_name);

    // Reset Data
    this.formTechDetails = { fullName: '', phone: '', plumbingLicence: '', drainageLicence: '', gasLicence: '' };
    this.formSelections = {
      pipework: {}, hydraulicInst: {}, hydraulicSetout: {}, hydraulicTest: {}, drainageInst: {}, drainageFind: {},
      hydraulicSetoutSF: {}, tapwareFitoff: {}, gasWaterHeater: {}
    };
    this.formNotes = '';
    this.formSignature = '';
    this.signatureEmpty = true;
    this.formSignatureDate = new Date().toISOString().split('T')[0];

    // Hydrate
    if (phase.form_data) {
      const tech = phase.form_data.tech_details || phase.form_data.techDetails;
      const selections = phase.form_data.checks || phase.form_data.selections;
      if (tech) this.formTechDetails = { ...this.formTechDetails, ...tech };
      if (selections) this.formSelections = { ...this.formSelections, ...selections };
      this.formNotes = phase.form_data.notes || '';
      this.formSignature = phase.form_data.signature || '';
      if (this.formSignature) this.signatureEmpty = false;
      this.formSignatureDate = phase.form_data.date || this.formSignatureDate;
    }

    this.showPhaseFormModal.set(true);

    // Delay canvas initialization to ensure view is rendered
    setTimeout(() => {
      if (this.signaturePad) this.initCanvas();
    }, 100);
  }

  // Signature PAD Logic
  initCanvas() {
    const canvas = this.signaturePad.nativeElement;
    // Retina scaling for sharper signatures
    const rect = canvas.parentElement!.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = 160 * 2; // Fixed height container internal height
    canvas.style.width = '100%';
    canvas.style.height = '160px';
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(2, 2);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#1e293b';

      // Restore signature if viewing existing 
      if (!this.signatureEmpty && this.formSignature && this.formSignature.startsWith('data:image')) {
        const img = new Image();
        img.onload = () => { ctx.drawImage(img, 0, 0, rect.width, 160); };
        img.src = this.formSignature;
      }
    }
  }

  getCanvasPos(e: MouseEvent | TouchEvent) {
    const canvas = this.signaturePad.nativeElement;
    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;

    if (e instanceof MouseEvent) {
      clientX = e.clientX;
      clientY = e.clientY;
    } else {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
      e.preventDefault(); // Stop scrolling when drawing on touch
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  }

  startDrawing(e: MouseEvent | TouchEvent) {
    this.isDrawing = true;
    this.signatureEmpty = false;
    this.lastCanvasPos = this.getCanvasPos(e);
  }

  draw(e: MouseEvent | TouchEvent) {
    if (!this.isDrawing) return;
    const pos = this.getCanvasPos(e);
    const ctx = this.signaturePad.nativeElement.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(this.lastCanvasPos.x, this.lastCanvasPos.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    }
    this.lastCanvasPos = pos;
  }

  endDrawing() {
    this.isDrawing = false;
  }

  clearSignature() {
    const canvas = this.signaturePad.nativeElement;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    this.signatureEmpty = true;
    this.formSignature = '';
  }

  toggleSelection(group: string, option: string) {
    this.formSelections[group][option] = !this.formSelections[group][option];
  }

  savePhaseForm() {
    if (this.isArchived()) {
      showToast('error', 'Archived projects are view-only.');
      return;
    }
    // Generate base64 if canvas is NOT empty
    if (!this.signatureEmpty && this.signaturePad) {
      this.formSignature = this.signaturePad.nativeElement.toDataURL('image/png');
    }

    this.isSaving.set(true);
    const payload = {
      phase_id: this.activePhaseId(),
      workflow_action: 'submit',
      form_data: {
        tech_details: this.formTechDetails,
        techDetails: this.formTechDetails,
        checks: this.formSelections,
        selections: this.formSelections,
        notes: this.formNotes,
        signature: this.formSignature,
        date: this.formSignatureDate
      }
    };
    this.api.saveProjectPhaseForm(payload).subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (res.success) {
          showToast('success', 'Form data saved safely');
          this.showPhaseFormModal.set(false);
          this.loadProject(this.project()?.id);
        } else {
          showToast('error', res.message || 'Error saving form');
        }
      },
      error: () => {
        this.isSaving.set(false);
        showToast('error', 'Network error tracking form');
      }
    });
  }

  downloadFirstFixPdf(phase_id: number) {
    showToast('success', 'Generating First Fix Document...');
    this.openProjectPdf('first-fix', phase_id);
  }

  downloadSecondFixPdf(phase_id: number) {
    showToast('success', 'Generating Second Fix Document...');
    this.openProjectPdf('second-fix', phase_id);
  }

  switchTab(tab: 'phases' | 'documents' | 'forms' | 'worklog' | 'photos' | 'timeline') {
    this.activeTab.set(tab);
  }

  loadProjectPhotos(id: string) {
    this.isLoadingProjectPhotos.set(true);
    this.api.getProjectPhotos(id).subscribe({
      next: (res: any) => {
        this.projectPhotos.set(res?.success ? (res.data?.photos || []) : []);
        this.isLoadingProjectPhotos.set(false);
      },
      error: () => this.isLoadingProjectPhotos.set(false)
    });
  }

  selectedProjectPhoto(): any | null {
    const index = this.projectPhotoViewerIndex();
    return index === null ? null : this.projectPhotos()[index] || null;
  }

  openProjectPhotoViewer(index: number) {
    if (index >= 0 && index < this.projectPhotos().length) this.projectPhotoViewerIndex.set(index);
  }

  closeProjectPhotoViewer() {
    this.projectPhotoViewerIndex.set(null);
  }

  showPreviousProjectPhoto() {
    const total = this.projectPhotos().length;
    const current = this.projectPhotoViewerIndex();
    if (total && current !== null) this.projectPhotoViewerIndex.set((current - 1 + total) % total);
  }

  showNextProjectPhoto() {
    const total = this.projectPhotos().length;
    const current = this.projectPhotoViewerIndex();
    if (total && current !== null) this.projectPhotoViewerIndex.set((current + 1) % total);
  }

  @HostListener('window:keydown', ['$event'])
  handleProjectPhotoViewerKeys(event: KeyboardEvent) {
    if (!this.selectedProjectPhoto()) return;
    if (event.key === 'Escape') this.closeProjectPhotoViewer();
    else if (event.key === 'ArrowLeft') this.showPreviousProjectPhoto();
    else if (event.key === 'ArrowRight') this.showNextProjectPhoto();
  }

  downloadSelectedProjectPhoto() {
    const photo = this.selectedProjectPhoto();
    if (!photo?.id || this.downloadingProjectPhotoId() !== null) return;
    this.downloadingProjectPhotoId.set(Number(photo.id));
    this.api.downloadPhoto(photo.id).subscribe({
      next: blob => {
        this.downloadingProjectPhotoId.set(null);
        const extension = blob.type === 'image/png' ? 'png' : blob.type === 'image/webp' ? 'webp' : blob.type === 'image/gif' ? 'gif' : 'jpg';
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = `project-photo-${photo.id}.${extension}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      },
      error: () => {
        this.downloadingProjectPhotoId.set(null);
        showToast('error', 'Photo could not be downloaded.');
      }
    });
  }

  private maybeOpenRequestedCertificate() {
    if (!this.requestedCertType || this.requestedCertOpened || !this.project()) return;
    this.requestedCertOpened = true;
    this.activeTab.set('forms');
    this.openCertForm(this.requestedCertType);
  }

  loadProject(id: string) {
    this.isLoading.set(true);
    // Fetch Project and Declaration status in parallel conceptually
    this.api.getProject(id).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.project.set(res.data);
          this.maybeOpenRequestedCertificate();
        }
        this.isLoading.set(false);
      },
      error: (err: any) => {
        this.isLoading.set(false);
        // showToast('error', 'Failed to load project details');
      }
    });

    this.api.getProjectDeclaration(id).subscribe({
      next: (res: any) => {
        if (res.success && res.data.is_signed) {
          this.declarationStatus.set('Signed');
          this.declarationData.set(res.data.declaration);
        } else if (res.success && res.data.declaration?.status === 'Skipped') {
          this.declarationStatus.set('Skipped');
          this.declarationData.set(res.data.declaration);
        } else if (res.success && res.data.declaration?.status === 'Sent') {
          this.declarationStatus.set('Sent');
          this.declarationData.set(res.data.declaration);
        } else {
          this.declarationStatus.set('Unsigned');
          this.declarationData.set(res.success ? res.data.declaration : null);
        }
      }
    });
  }

  copyDeclarationLink() {
    const token = this.declarationData()?.public_token;
    if (!token) {
      showToast('error', 'Declaration link is still being prepared. Please try again in a moment.');
      this.loadProject(this.projectId);
      return;
    }
    const link = `${window.location.origin}/declaration/${token}`;
    navigator.clipboard.writeText(link).then(() => {
      showToast('success', 'Declaration link copied to clipboard!');
    }).catch(() => {
      showToast('error', 'Failed to copy link natively.');
    });
  }

  sendDeclarationEmail() {
    this.isSendingEmail.set(true);
    this.api.sendDeclarationEmail(this.projectId).subscribe({
      next: (res: any) => {
        if (res.success) {
          showToast('success', 'Declaration securely sent to customer!');
          this.declarationStatus.set('Sent');
          this.loadProject(this.projectId); // reload to get customer details
        } else {
          showToast('error', res.message || 'Failed to dispatch email matrix.');
        }
        this.isSendingEmail.set(false);
      },
      error: () => {
        showToast('error', 'Network error whilst dispatching email.');
        this.isSendingEmail.set(false);
      }
    });
  }

  skipDeclaration() {
    this.showSkipDeclarationModal.set(true);
  }

  closeSkipDeclarationModal() {
    if (!this.isSkippingDeclaration()) this.showSkipDeclarationModal.set(false);
  }

  confirmSkipDeclaration() {
    if (this.isSkippingDeclaration()) return;
    this.isSkippingDeclaration.set(true);
    this.api.skipProjectDeclaration(this.projectId).subscribe({
      next: (res: any) => {
        this.isSkippingDeclaration.set(false);
        if (res.success) {
          showToast('success', 'Declaration skipped. First Fix can now progress.');
          this.declarationStatus.set('Skipped');
          this.showSkipDeclarationModal.set(false);
          this.loadProject(this.projectId);
        } else {
          showToast('error', res.message || 'Declaration could not be skipped.');
        }
      },
      error: (err: any) => {
        this.isSkippingDeclaration.set(false);
        showToast('error', err?.error?.message || 'Network error skipping declaration.');
      }
    });
  }

  downloadDeclarationPdf() {
    this.openProjectPdf('declaration', this.projectId);
  }

  private openProjectPdf(docType: string, id: string | number) {
    if (!docType || !id) return;
    this.router.navigate(['/admin/project-pdf', docType, String(id)], {
      queryParams: { projectId: this.projectId }
    });
  }

  onFileSelected(event: any) {
    const file: File = event.target.files[0];
    if (file) {
      this.isUploading.set(true);
      const formData = new FormData();
      formData.append('document', file);
      formData.append('project_id', this.project().id);
      formData.append('name', file.name);
      formData.append('document_type', 'General');

      this.api.uploadProjectDocument(formData).subscribe({
        next: (res: any) => {
          this.isUploading.set(false);
          if (res.success) {
            showToast('success', 'Document uploaded securely');
            this.loadProject(this.project().id); // Refresh grid organically
          } else {
            showToast('error', res.message || 'Upload failed natively');
          }
        },
        error: () => {
          this.isUploading.set(false);
          showToast('error', 'Network error tracking document');
        }
      });
    }
  }
}

